/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components

// This is where we will display the conversation details
// Convo Name and AI Stuff, record button, etc
/* global chrome */

import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Container,
  Label,
  Row,
  UncontrolledTooltip as Tooltip,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';

import Modal from 'components/Modals/Modal';
import PatientModal from 'components/Modals/PatientModal';
import InstructionAssistantModal from 'components/InstructionAssistant/InstructionAssistantModal';
import InstructionUpdateStatusStrip from 'components/InstructionAssistant/InstructionUpdateStatusStrip';
import InstructionUpdateUndoModal from 'components/InstructionAssistant/InstructionUpdateUndoModal';
import { useInstructionAssistantUpdate } from 'components/InstructionAssistant/useInstructionAssistantUpdate';
import { createVisitNoteTools } from 'components/ChatAgent/visitNoteTools';
import { buildSectionMetadata, compactFlexCurrentAnswer } from 'components/InstructionAssistant/instruction-assistant-utils';
import AudioRecorder from '../api/Recorder.js';
import {
  answerQuestionInEMR,
  answerQuestionsInEMR,
  getQuestionsFromEMR,
  getPatientInfo,
  createEMRSession,
  getPageInfo,
  getPageMetadata,
  highlightFields,
  injectComplianceBadge,
  removeComplianceBadge,
  reportComplianceScoreToRail,
  setupCompliancePenClickCallback,
} from '../EMR_Integration/react_webInjector.ts';

// core components
import Header from 'components/Headers/Header';
import Chat from '../components/Misc/Chat';
import Coding from '../components/Misc/Coding';
import Collapsible, {
  WriteNotesCollapsible,
} from '../components/Misc/Collapsible';
import ComplianceScore, { scoreToGrade } from '../components/Misc/ComplianceScore';
import ComprehendFeedback, { FEEDBACK_SOURCES } from '../components/Misc/ComprehendFeedback';
import {
  extractExistingValuesOnly,
  handleGetComplianceScore as handleGetComplianceScoreUtil,
  getScoreRefreshLabel,
  getScoreSurfaceLabel,
} from '../components/Misc/complianceUtils.js';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import DictationButton from '../components/Misc/DictationButton';
import { OptionsDropdown } from '../components/Misc/ConversationDetailsUtils.js';
import EditableLabel from '../components/Misc/EditableLabel';
import getFullName from '../components/Misc/getFullName';
import OutlinedSelector from '../components/Misc/OutlinedSelector';
import EmrConnecting from '../components/Misc/EmrConnecting';
import ShareTemplateModal from '../components/Misc/ShareTemplateModal';
import HashState from '../variables/hash';
// Tool Magic
import {
  createChangePatientTool,
  createComprehendTool,
} from '../components/Misc/Synapse/ConversationDetailsTools.js';
import { FAKE_TRANSCRIPT } from '../components/Misc/Synapse/Prompts.js';
import { isDemoMode } from '../utils/demoStorage';
import {
  learnResultBanner,
  learnTemplateSections,
} from '../utils/learnTemplate';
import { saveLearnExample } from '../utils/learnExample';
import { isAgenticLearnUser } from '../utils/agenticLearn';
import { parallelSectionReplyType } from '../utils/parallelSections';
import { subsectionNamesToSave } from '../utils/subsectionNames';
import {
  integrationUpgradeFor,
  withLegacySubsectionInstructions,
} from '../utils/legacySubsectionInstructions';

import LoadingBanner from '../components/Misc/LoadingBanner.js';
import { cacheManager } from '../utils/CacheManager';
import {
  onAddSample,
  onEditSample,
  onRemoveSample,
} from '../components/Misc/samplesUtil.js';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { logGAEvent } from '../api/googleAnalytics';
import webpt2DropdownChoicesJson from '../assets/webpt2DropdownChoices.js';
import NavigationPath from '../components/Misc/NavigationPath';
import { truncateText } from '../components/Misc/utils';
import { toast as showToast } from 'react-toastify';
import { checkTranscript } from '../utils/transcriptCheck';
import vendorStore, { patientFromRecord, isVendorAppsEnabled } from '../utils/vendorApps/store';
import VendorSubscriptionsSection from '../components/VendorApps/VendorSubscriptionsSection';
import useVisitWorkspace from '../components/Workspace/useVisitWorkspace';
import { renderWorkspaceTabs } from '../components/Workspace/WorkspaceTabs';
import { complianceCompactTitle } from '../components/Workspace/ItemGlyph';
import WorkspaceIcon from '../components/Workspace/WorkspaceIcon';
import { APPS_ID } from '../utils/workspace/workspaceLayout';
import AppData from '../components/VendorApps/AppData';
import { GRADE_TEXT_COLORS, gradeForScore } from '../components/Misc/complianceGrades';

const LEARN_FEATURE_FLAG = 'learn';
const PATIENT_MATCH_WARNING_FLAG = 'patient_match_warning';
const PATIENT_MATCH_DISMISSED_KEY = 'patientMatchWarningDismissed';
const patientWarningDismissed = () => {
  try {
    return localStorage.getItem(PATIENT_MATCH_DISMISSED_KEY) === 'true';
  } catch (e) {
    return false;
  }
};
const COMPREHEND_FEEDBACK_FLAG = 'comprehend_feedback';
// Headless Comprehend gives up if the visit isn't ready to Comprehend by then.
const HEADLESS_START_TIMEOUT_MS = 60 * 1000;
const CONFLUENT_ASSESSMENT_DETAILS_SECTION = 'Assessment - Details';
const CONFLUENT_ASSESSMENT_DETAILS_BRANCH = 'Summary of Findings';
const CONFLUENT_ASSESSMENT_DETAILS_KEEP = [
  'Assessment Narrative',
  'Diagnosis',
  'Complicating Factors',
  'Competing Hypotheses',
  'HEP',
];

/**
 * Reduce a Confluent Assessment - Details structure to the five allow-listed
 * fields under "Summary of Findings", dropping every other branch.
 *
 * The allowlist has to be scoped to that branch, not matched on field label
 * alone: sibling branches carry a field ALSO called "Assessment Narrative"
 * (e.g. "Assessment - Auth Required/Progress Note - Assessment Narrative"), so
 * a label-only match lets them through.
 *
 * Falls back to the untouched structure if the branch or the fields aren't
 * found — the template has changed shape at that point, and returning an empty
 * structure would silently blank the section instead of just skipping fields.
 */
function filterToConfluentAssessmentFields(structure) {
  if (!structure || typeof structure !== 'object' || Array.isArray(structure)) {
    return structure;
  }

  const allowed = new Set(
    CONFLUENT_ASSESSMENT_DETAILS_KEEP.map((label) => label.toLowerCase()),
  );
  // Leaf keys arrive fully qualified: "Assessment - <branch> - <field>".
  const trailingLabel = (key) => {
    const parts = String(key).split(' - ');
    return parts[parts.length - 1].trim().toLowerCase();
  };

  const branchKey = Object.keys(structure).find(
    (key) =>
      String(key).trim().toLowerCase() ===
      CONFLUENT_ASSESSMENT_DETAILS_BRANCH.toLowerCase(),
  );
  if (!branchKey) {
    console.warn(
      `Confluent: no "${CONFLUENT_ASSESSMENT_DETAILS_BRANCH}" branch in ${CONFLUENT_ASSESSMENT_DETAILS_SECTION}; sending the full structure`,
    );
    return structure;
  }

  const branch = structure[branchKey];
  if (!branch || typeof branch !== 'object' || Array.isArray(branch)) {
    return structure;
  }

  const keptFields = {};
  Object.keys(branch).forEach((fieldKey) => {
    if (allowed.has(trailingLabel(fieldKey))) {
      keptFields[fieldKey] = branch[fieldKey];
    } else {
      console.log(`Confluent: skipping field ${fieldKey}`);
    }
  });

  if (Object.keys(keptFields).length === 0) {
    console.warn(
      'Confluent: Assessment - Details allowlist matched no fields; sending the full structure',
    );
    return structure;
  }

  Object.keys(structure).forEach((key) => {
    if (key !== branchKey) {
      console.log(`Confluent: skipping branch ${key}`);
    }
  });

  return { [branchKey]: keptFields };
}

// headless: mounted hidden by the visits list to Comprehend `visitId` in place.
const ConversationDetails = ({
  visitId,
  headless = false,
  skipPatientCheck = false,
  onDone,
} = {}) => {
  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  const navigate = useNavigate();
  const location = useLocation();
  const libraryConfigId = location.state?.configId;

  const learnFeatureFlag = apiClient.features[LEARN_FEATURE_FLAG];

  const [activeTab, setActiveTab] = useState('response'); // Track active tab
  // The landing tab is chosen once, on the first details load. After that the
  // clinician's own tab choice stands.
  const landingTabChosenRef = useRef(false);
  const [tabsVisible, setTabsVisible] = useState(false); // Control tab visibility

  // Default Values for the fields
  const defaultConversationID = -1;
  const defaultName = 'Conversation Name';
  const defaultStatus = 'Loading...';
  const defaultConfig = 'Default';
  const defaultConfigID = -1;
  const defaultGroup = 'None';
  const defaultSecondsTranscribed = 0;
  const defaultGroupId = -1;
  const defaultTranscript = '';
  const defaultVisitNotes = '';
  const defaultTemplate = {};
  const defaultTemplateReply = {};
  const defaultSamples = {};
  const defaultSubsections = {};

  // State Variables
  const [conversationID, setConversationID] = useState(defaultConversationID); // This is the ID of the conversation we are looking at
  const [refresh, _setRefresh] = useState(false); // This is used to force a refresh of the conversation fields (like when we change the config)
  const [name, _setName] = useState(defaultName);
  const [status, _setStatus] = useState(defaultStatus);
  const [config, _setConfig] = useState(defaultConfig);
  const [configID, _setConfigID] = useState(defaultConfigID);

  const [group, __setGroup] = useState(defaultGroup);
  const [groupId, _setGroupId] = useState(defaultGroupId);
  const [transcript, _setTranscript] = useState(defaultTranscript);
  const [visitNotes, _setVisitNotes] = useState(defaultVisitNotes);
  const [appContext, _setAppContext] = useState({}); // connected apps' context for this visit, keyed by app id (its own field)
  const [secondsTranscribed, _setSecondsTranscribed] = useState(
    defaultSecondsTranscribed,
  );
  const [template, _setTemplate] = useState(defaultTemplate);
  const [templateReply, _setTemplateReply] = useState(defaultTemplateReply);
  const replyTypeRevisionsRef = useRef({});
  const templateReplyConfigIDRef = useRef(defaultConfigID);
  const [samples, _setSamples] = useState(defaultSamples);
  const [subsections, _setSubsections] = useState(defaultSubsections);
  const [viewMode, setViewMode] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharedAccessData, setSharedAccessData] = useState([]);
  const [userEmail, setUserEmail] = useState(null);
  const [showTemplateHelpModal, setShowTemplateHelpModal] = useState(false);
  const [questionBatching, setQuestionBatching] = useState(false);
  const [existingNote, setExistingNote] = useState(false);

  // This is the template filtered to only show the fields in view of the EMR right now
  const [templateFiltered, _setTemplateFiltered] = useState(defaultTemplate);
  const routeHash = HashState();
  const hash = visitId != null ? String(visitId) : routeHash;
  const hashRef = useRef(hash);
  hashRef.current = hash;
  // Get all user Groups
  const defaultGroups = ['None', 'New'];
  const [uniqueGroups, setUniqueGroups] = useState(defaultGroups);
  const [isPatientModalOpen, setPatientModelOpen] = useState(false);
  // Get all user Configs
  const [uniqueConfigs, setUniqueConfigs] = useState([]);
  // Delete confirmation prompt
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    type: '', // 'sample' or 'template'
    questionKey: '',
    index: -1,
  });
  const [aiThinking, setAiThinking] = useState(false);
  const [instructionAssistantOpen, setInstructionAssistantOpen] = useState(false);
  const [instructionAssistantFeedback, setInstructionAssistantFeedback] =
    useState('');
  // Snapshot of the current EMR scrape. The Instruction Assistant refreshes this
  // before planning and reuses it for section metadata and note undo.
  const lastScrapedQuestionsRef = useRef(null);
  const [bannerText, setBannerText] = useState('Comprehending...');
  const [popupVisible, setPopupVisible] = useState(false);
  const [isDowngraded, setIsDowngraded] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);
  const [patientMismatch, setPatientMismatch] = useState(null);
  const mismatchRef = useRef(null); // headless: hands a mismatch back to the list
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalHeader, setModalHeader] = useState(
    'Personalized or Standard Note?',
  );
  const [modalButtons, setModalButtons] = useState('');
  const [showEditorFields, setShowEditorFields] = useState(false);
  const [showWriteNotes, setShowWriteNotes] = useState(
    () => localStorage.getItem('writeNotesOpen') === 'true',
  );
  const toggleWriteNotes = () => {
    const next = !showWriteNotes;
    setShowWriteNotes(next);
    localStorage.setItem('writeNotesOpen', next);
  };
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // State for the dropdown menu
  const [showDropdown, setShowDropdown] = useState(false);

  // State for compliance score and feedback
  const [complianceScore, _setComplianceScore] = useState(0); // Default to 0
  const [complianceFeedback, _setComplianceFeedback] = useState([]); // Default to empty
  const [complianceScoreLoading, setComplianceScoreLoading] = useState(false);
  // Scorecard flag: "Scorecard" copy when on, "Compliance" copy when off.
  const scoreSurfaceLabel = getScoreSurfaceLabel(apiClient);
  const scoreRefreshLabel = getScoreRefreshLabel(apiClient);
  // Whether this visit gets the compliance surface at all. The note preference
  // is the usual gate, but a visit that already has a grade -- scored from the
  // compliance rail, say -- needs somewhere to show it regardless. Used for both
  // the tab and its contents so the two can never disagree.
  const canShowCompliance = apiClient.showComplianceScore || complianceScore > 0;

  const [emrUndoStack, setEmrUndoStack] = useState([]);
  const [showHighlightModal, setShowHighlightModal] = useState(false);
  // { id, conversationId, emrHost, noteType } for the last Comprehend button click that finished; drives the feedback bar.
  const [feedbackRun, setFeedbackRun] = useState(null);
  const inputRef = useRef();
  const setRefresh = () => _setRefresh(!refresh);
  const isConfluentUser = apiClient.isConfluentUser;
  const complianceSignOffGate = isConfluentUser && !!apiClient.features['compliance_sign_off_gate'];
  const patientMatchWarning = !!apiClient.features[PATIENT_MATCH_WARNING_FLAG];

  // Inject compliance badge onto Confluent EMR sign-off pen (Confluent only)
  useEffect(() => {
    if (!isConfluentUser || !apiClient.showComplianceScore) return;
    if (complianceScoreLoading) return; // Re-check in flight — loading modal shown by pen click handler, don't touch it
    if (!complianceScore) {
      // COM-427: a failed re-check must not leave the pen-click flag set -- the
      // next grade A would auto-complete a sign-off nobody asked for.
      penRecheckPendingRef.current = false;
      removeComplianceBadge();
      return;
    }
    const grade = scoreToGrade(complianceScore);
    const autoShow = penRecheckPendingRef.current;
    penRecheckPendingRef.current = false;
    injectComplianceBadge(grade, complianceFeedback, autoShow, complianceSignOffGate);
    // No cleanup return — injectComplianceBadge calls removeComplianceBadge() itself on next run
  }, [complianceScore, complianceFeedback, isConfluentUser, apiClient.showComplianceScore, complianceScoreLoading, complianceSignOffGate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove badge on unmount
  useEffect(() => () => { removeComplianceBadge(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a grade produced here onto the Free Compliance rail in the EMR tab.
  // Only grades from a scoring run that just finished in this panel: a score
  // loaded with the visit may belong to a note that is no longer on screen.
  const complianceWasLoadingRef = useRef(false);
  useEffect(() => {
    const justFinished = complianceWasLoadingRef.current && !complianceScoreLoading;
    complianceWasLoadingRef.current = complianceScoreLoading;
    if (justFinished && complianceScore > 0) {
      reportComplianceScoreToRail(conversationID, complianceScore, complianceFeedback);
    }
  }, [complianceScoreLoading, complianceScore, complianceFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the tutorial only for first-time users
  // First-run tutorial popup (the hosted slide deck) no longer auto-opens: the
  // onboarding checklist is the guide now. It stays reachable from the sidebar's
  // "Getting started".

  /**
   * Tool Magic
   */
  // Create a single ref to hold all action handlers for external access (tools, etc)
  const actionsRef = useRef({});

  useEffect(() => {
    // Initialize the OpenAIAssistant
    const comprehendTool = createComprehendTool(async () => {
      // Only set fake transcript if current transcript has no real content (just date/patient info)
      const currentTranscript = actionsRef.current.transcript || '';
      const wordCount = currentTranscript
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      const lineCount = currentTranscript.trim().split('\n').length;
      const hasOnlyBasicInfoLines = currentTranscript
        .trim()
        .split('\n')
        .every((line) => line.includes(':')); // all the default lines have a colon in them
      const hasOnlyBasicInfo =
        isDemoMode() &&
        wordCount <= 25 &&
        lineCount <= 3 &&
        hasOnlyBasicInfoLines;
      if (hasOnlyBasicInfo) {
        await actionsRef.current.setTranscript(FAKE_TRANSCRIPT);
      }
      // await setTranscript(apiClient.transcript); (Should totally append and also consider a cleaning mechanism)
      actionsRef.current.comprehendTemplate();
    });
    apiClient.addTools([comprehendTool]);
    return () => {
      // Unregister the tool magic with the API client
      apiClient.removeTools([comprehendTool.name]);
    };
  }, []);

  // Mount transcript as dynamic context - updates whenever transcript changes
  useEffect(() => {
    if (!transcript || transcript.trim() === '') return; // Don't add empty transcript

    apiClient.addContext('current_transcript', {
      transcript: transcript,
      wordCount: transcript
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length,
      conversationId: conversationID,
    });

    return () => {
      apiClient.removeContext('current_transcript');
    };
  }, [transcript, conversationID]);

  // Mount patient prior visit details as dynamic context - updates when patient changes
  useEffect(() => {
    if (!groupId || groupId === -1) return;
    apiClient.getPatientHistory(groupId, 3).then((data) => {
      if (!data || data.error) return;
      apiClient.addContext('current_patient_prior_visit_details', {
        ...data,
        conversationId: conversationID,
      });
    });
    return () => apiClient.removeContext('current_patient_prior_visit_details');
  }, [groupId, conversationID]);

  // Public functions to update the state variables AND send to the server

  const setName = (newName) => {
    _setName(newName);
    apiClient.editConversation(conversationID, 'name', newName);
  };

  async function userChangedConfig(newConfig) {
    // This function is called when the user selects a new config from the dropdown
    // We need to lookup the config id based on the newConfig name and set the configId to the uid
    // These don't happen until the function completes... so it's basicly a mute point but a good idea for sure...
    // _setTemplate({})
    // _setSamples({})
    setDisableComprehend(true);
    _setStatus('Loading Template..');
    logGAEvent('template_selected', apiClient, {
      config_id: newConfig.configId,
    });
    console.log(newConfig);
    await setConfig(newConfig);
    // Now we need to go get new questions for this config...
    _setStatus('Scanning EMR...');
    // TODO RIGHT HERE< THE NEW CONFIG IS THE NAME OF THE CONFIG NOT THE ACTUAL ID WHICH IS WHAT WE NEED
    // We should have a cache of all our configs someherew... if not, do it and look there for it
    // check the apiClient.configs for one matching where it's a list and one of the objects should have a name with the newConfig
    var newConfigId = apiClient.configs.filter(
      (config) => config.name === newConfig,
    )[0].configId;
    console.log(apiClient.configs);
    await loadTemplate(false, newConfigId);

    // TODO Handle view mode on ReadOnly...
    _setStatus('Ready!');
    setDisableComprehend(false);
  }

  // TODO, probs don't do this...
  const setConfig = (newConfig, conversationID, configID) => {
    // Add the new entry to the list of unique groups (It will already be there....)
    // setUniqueConfigs([...new Set([...uniqueConfigs, newConfig])]);

    // Set the local copy and refresh the questions
    _setConfig(newConfig);

    // Send the change to the server and lookup the fields for the new config
    return apiClient
      .editConversation(conversationID, 'configId', configID)
      .catch((data) => {
        alert('Failed to update config, Try again.');
      })
      .finally(() => {
        setRefresh();
      }); // Update the questions
  };

  const _setGroup = (newGroup) => {
    /*
          Description: 
              This function is called when the user selects a new group from the dropdown
          Args:
              newGroup: The name of the new group
          
          Notes:
              We need to lookup the group id based on the newGroup name and set the groupId to the uid
      */

    // Check if the group is new and if so create a modal asking the user to create a new group
    if (newGroup === 'New') {
      setPatientModelOpen(true);
      setShowEditorFields(false);
      return;
    }

    // Lookup the group id
    // If we found it, set the ID and Name
    var patientID = apiClient.patients.filter(
      (patient) => getFullName(patient) === newGroup,
    )[0];
    if (patientID === undefined) {
      // console.log(`Could not find patient with name ${newGroup}`);
      // Not ready yet, and that's okay
      return;
    }
    patientID = patientID.uid;

    __setGroup(newGroup);
    _setGroupId(patientID);
    return patientID;
  };

  const setGroup = (newGroup) => {
    if (newGroup == 'None') {
      var patientID = -1;
    } else {
      var patientID = _setGroup(newGroup);
    }
    // console.log(`patientID = ${patientID}`)
    if (patientID || newGroup == 'None') {
      // We should send this change to the server
      apiClient
        .editConversation(conversationID, 'group', patientID)
        .then((data) => {
          if ('error' in data) {
            console.log('Error editing conversation');
            console.log(data);
            return;
          }
        })
        .catch((data) => {
          // Update the questions
          setRefresh();
          alert('Failed to update Patient, Try again.');
        });

      // Add the new entry to the list of unique groups...
      // setUniqueGroups([...new Set([...uniqueGroups, patientInfo])]);
      // Update the user's patient list from the server after successfully making the new patient
    }
  };

  const openConfirmationModal = (type, questionKey, index) => {
    setConfirmationDetails({ isOpen: true, type, questionKey, index });
  };

  const handleConfirmAction = () => {
    if (confirmationDetails.type === 'sample') {
      onRemoveSample(
        confirmationDetails.questionKey,
        confirmationDetails.index,
        configID,
        _setSamples,
        apiClient,
      );
    }
    setConfirmationDetails({ ...confirmationDetails, isOpen: false });
  };

  // flex++ leaves carry only `_` keys; mirror the tree with each _existingValue
  // so undo can hand the same nested shape back to the injector.
  function flattenFlexPPStructure(node) {
    if (Array.isArray(node)) return node.map(flattenFlexPPStructure);
    if (!node || typeof node !== 'object') return '';
    const keys = Object.keys(node);
    if (keys.length > 0 && keys.every((k) => k.startsWith('_'))) {
      return node._existingValue ?? '';
    }
    const result = {};
    keys.forEach((k) => {
      if (k.startsWith('_') || k === 'flex++' || k === 'prune_early') return;
      result[k] = flattenFlexPPStructure(node[k]);
    });
    return result;
  }

  function flattenFlexStructure(snapshotStructure, parallelSections = []) {
    if (snapshotStructure?.['flex++'] === true) {
      return flattenFlexPPStructure(snapshotStructure);
    }
    const result = {};
    for (const fieldKey in snapshotStructure) {
      const fieldValue = snapshotStructure[fieldKey];
      // A clinical subsection published as rows
      // sits one level deeper than a field, so flatten its rows the same way;
      // otherwise undo would hand its filler empty strings instead of rows.
      if (parallelSections.includes(fieldKey) && Array.isArray(fieldValue)) {
        result[fieldKey] = flattenFlexStructure(fieldValue);
        continue;
      }
      result[fieldKey] = {};
      for (const subKey in fieldValue) {
        const fieldObj = fieldValue[subKey];
        result[fieldKey][subKey] =
          fieldObj &&
          typeof fieldObj === 'object' &&
          'Existing Value' in fieldObj
            ? fieldObj['Existing Value']
            : '';
      }
    }
    return result;
  }

  // Helper: remove listed subsection keys from a nested Structure while preserving arrays
  function filterStructureExcludingSubsections(structure, subsectionsToRemove) {
    if (!structure || typeof structure !== 'object') return structure;
    if (Array.isArray(structure)) return structure.slice();

    const result = {};
    Object.keys(structure).forEach((key) => {
      if (!subsectionsToRemove.includes(key)) {
        const value = structure[key];
        if (value && typeof value === 'object') {
          result[key] = Array.isArray(value)
            ? value.slice()
            : filterStructureExcludingSubsections(value, subsectionsToRemove);
        } else {
          result[key] = value;
        }
      } else {
        console.log(`Removing subsection: ${key}`);
      }
    });
    return result;
  }

  /**
   * Before sending data to the backend, we remove questions tied to
   * subsections the user has marked as "Skip" (no_reply). This saves tokens
   * and prevents unnecessary content from being processed.
   * {"Basic Info": {"Basic Info - Subjective": {"Existing Value": "", "Input": (val) => {input the value into the input box}}}}
   * {"Basic Info": {"Basic Info - Subjective": {...}, "Basic Info - Subjective2": {...}}}
   * {"Basic Info": {...}, "Basic Info 2": {...}}
   * {"Basic Info - Subjective": {...}, "Basic Info - Subjective2": {...}}
   * @param {object} question - The question object (from localQuestions[key])
   * @param {string|object} templateValue - The template value (may be JSON string or object)
   * @returns {object} filtered structure ready to send to the API
   */
  function filterFlexStructureForNoReply(question, templateValue) {
    let structure = question['Structure'] || {};

    // Only apply filtering for Flex questions with a defined template
    if (question['Type'] === 'Flex' && templateValue) {
      let parsedTemplate;
      try {
        parsedTemplate =
          typeof templateValue === 'string'
            ? JSON.parse(templateValue)
            : templateValue;
      } catch (e) {
        console.log('Failed to parse template value:', templateValue);
        parsedTemplate = null;
      }

      if (parsedTemplate && parsedTemplate.SubSections) {
        // Deep clone the structure to avoid mutating original
        structure = JSON.parse(JSON.stringify(structure));

        // Identify subsections flagged with replyType = no_reply
        const excludedSubsections = Object.keys(
          parsedTemplate.SubSections,
        ).filter(
          (subKey) =>
            parsedTemplate.SubSections[subKey] &&
            parsedTemplate.SubSections[subKey].replyType === 'no_reply',
        );

        // Use shared helper to filter those subsections out
        structure = filterStructureExcludingSubsections(
          structure,
          excludedSubsections,
        );
      }
    }

    // Confluent group only: Buck's template fills five Assessment - Details
    // fields, so everything else in that section is skipped regardless of what
    // the shared template says.
    if (
      isConfluentUser &&
      question['Type'] === 'Flex' &&
      question['Key'] === CONFLUENT_ASSESSMENT_DETAILS_SECTION
    ) {
      structure = filterToConfluentAssessmentFields(structure);
    }

    return structure;
  }

  /**
   * Compares an existing template instruction with a new template instruction (both may be strings
   * containing JSON or plain text). If both instructions are JSON objects with subsections,
   * performs a "smart diff" to detect only changed, added, or removed subsections.
   *
   * - If only subsections differ, queues a minimal update to `bulkEdit`.
   * - If only the top-level instruction differs, queues a minimal update to `bulkEdit`.
   * - If no changes are detected, skips queuing.
   * - If the old instruction was a simple string, converts it to a JSON structure and compares.
   * - If the new instruction is JSON but old parsing fails, sends the new JSON as-is.
   */
  function checkSectionAndSubsectionInstructionChange(
    previousInstruction,
    updatedInstruction,
    bulkEdit,
    key,
  ) {
    if (
      typeof previousInstruction === 'string' &&
      typeof updatedInstruction === 'string'
    ) {
      try {
        previousInstruction = JSON.parse(previousInstruction);
        updatedInstruction = JSON.parse(updatedInstruction);

        // If previous value was just a plain string, wrap it into JSON format
        if (
          typeof previousInstruction !== 'object' ||
          previousInstruction === null
        ) {
          previousInstruction = {
            TopLevelInstruction: previousInstruction,
            SubSections: {},
          };
        }

        if (previousInstruction.SubSections && updatedInstruction.SubSections) {
          const previousSubsections = previousInstruction.SubSections;
          const updatedSubsections = updatedInstruction.SubSections;

          const modifiedSubsections = {};
          let changesDetected = false;

          // Look for new or modified subsections
          Object.keys(updatedSubsections).forEach((subKey) => {
            const previousSubsection = previousSubsections[subKey];
            const updatedSubsection = updatedSubsections[subKey];

            if (!previousSubsection) {
              modifiedSubsections[subKey] = updatedSubsection;
              changesDetected = true;
            } else {
              const previousInstruction = previousSubsection.Instruction || '';
              const updatedInstruction = updatedSubsection.Instruction || '';
              const previousReplyType =
                previousSubsection.replyType || 'overwrite';
              const updatedReplyType =
                updatedSubsection.replyType || 'overwrite';

              if (
                previousInstruction !== updatedInstruction ||
                previousReplyType !== updatedReplyType
              ) {
                modifiedSubsections[subKey] = updatedSubsection;
                changesDetected = true;
              }
            }
          });

          // Look for removed subsections
          Object.keys(previousSubsections).forEach((subKey) => {
            if (!updatedSubsections[subKey]) {
              modifiedSubsections[subKey] = {
                Instruction: '',
                replyType: 'overwrite',
              };
              changesDetected = true;
            }
          });

          if (changesDetected) {
            const serializedUpdate = JSON.stringify({
              TopLevelInstruction: updatedInstruction.TopLevelInstruction,
              SubSections: updatedSubsections,
            });

            bulkEdit.push({
              fieldName: 'update_question_value',
              key,
              value: serializedUpdate,
            });
            return true; // skip default save
          } else {
            console.log('No changes detected, skipping update');
            return true;
          }
        }
      } catch (error) {
        // handle parsing errors
      }
    }
    return false;
  }

  const getEMRQuestionsWithCache = async (
    configIdLocal = null,
    session = null
  ) => {
    const pageInfo = session
      ? await session.getPageInfo()
      : await getPageInfo();
    if (!pageInfo) {
      return { questions: [], dropdownMisses: [], dropdownCache: {} };
    }
    const noteType = pageInfo['Note Type'];
    const isWebPT2User = pageInfo['URL'].includes('emr.webpt');
    const isTheraOfficeUser = pageInfo['URL'].includes('theraoffice.com');

    let localDropdownCache = {};
    let replyType = {};
    if (isWebPT2User || isTheraOfficeUser) {
      let jsonFile = isWebPT2User ? webpt2DropdownChoicesJson[noteType] : {};
      let dropdownCacheInLocalStorage = isWebPT2User
        ? localStorage.getItem('webpt2DropdownCache')
        : isTheraOfficeUser
          ? localStorage.getItem('theraOfficeDropdownCache')
          : null;

      let dropdownCacheInLocalStorageJson = dropdownCacheInLocalStorage
        ? JSON.parse(dropdownCacheInLocalStorage)
        : {};
      let noteTypeDropdownCache = {
        ...jsonFile,
        ...dropdownCacheInLocalStorageJson[noteType],
      };
      localDropdownCache = {
        ...dropdownCacheInLocalStorageJson,
        [noteType]: noteTypeDropdownCache,
      };
      localStorage.setItem(
        isWebPT2User ? 'webpt2DropdownCache' : 'theraOfficeDropdownCache',
        JSON.stringify(localDropdownCache),
      );
      replyType = await getReplyTypeForWebPT2(pageInfo, configIdLocal);
    }
    const emrQuestionsResult = session
      ? await session.getQuestionsFromEMR(
          apiClient.isWebPTUser,
          localDropdownCache,
          replyType,
          apiClient.empowerNewVersion,
          apiClient.newRaintreeIntegration,
          apiClient.newPromptIntegration,
          apiClient.coraRom,
          apiClient.newWebPTSoap2Integration,
          apiClient.webpt2ChromeFreezeFix
        )
      : await getQuestionsFromEMR(
          apiClient.isWebPTUser,
          localDropdownCache,
          replyType,
          apiClient.empowerNewVersion,
          apiClient.newRaintreeIntegration,
          apiClient.newPromptIntegration,
          apiClient.coraRom,
          apiClient.newWebPTSoap2Integration,
          apiClient.webpt2ChromeFreezeFix
        );
    let resultDropdownCache =
      emrQuestionsResult?.dropdownCache || localDropdownCache || {};
    localStorage.setItem(
      isWebPT2User || isTheraOfficeUser
        ? 'webpt2DropdownCache'
        : 'theraOfficeDropdownCache',
      JSON.stringify(resultDropdownCache),
    );

    // Snapshot the scraped questions (shallow-cloned, since scrapeTemplate later
    // reassigns element.Question for new sections) so the Instruction Assistant
    // can use the current scan for section metadata and note undo.
    const scrapedQuestions = emrQuestionsResult?.questions || [];
    lastScrapedQuestionsRef.current = scrapedQuestions.map((q) => {
      const currentAnswer =
        q.Type === 'Flex' && q.Structure
          ? flattenFlexStructure(q.Structure, q.ParallelSections)
          : q.ExistingNote;
      return {
        ...q,
        currentAnswer:
          isWebPT2User && q.Type === 'Flex'
            ? compactFlexCurrentAnswer(currentAnswer)
            : currentAnswer,
      };
    });
    return {
      questions: scrapedQuestions,
      dropdownMisses: emrQuestionsResult?.dropdownMisses || [],
      dropdownCache: resultDropdownCache,
      emrURL: pageInfo['URL'],
    };
  };

  const handleRevertChanges = async () => {
    let currentSession;
    try {
      currentSession = await createEMRSession();
    } catch (err) {
      console.error('Failed to create EMR session for revert:', err);
      _setStatus('Could not verify the current EMR note to revert.');
      return;
    }

    const sessionsMatch = (entrySession) => {
      if (!entrySession) return false;
      if (
        typeof currentSession.tabId === 'number' &&
        currentSession.tabId >= 0 &&
        typeof entrySession.tabId === 'number' &&
        entrySession.tabId >= 0
      ) {
        return (
          entrySession.tabId === currentSession.tabId &&
          entrySession.fingerprint === currentSession.fingerprint
        );
      }
      // Web/embed sessions do not have Chrome tab ids, so match on the
      // current page-context fingerprint instead.
      return (
        entrySession.fingerprint &&
        currentSession.fingerprint &&
        entrySession.fingerprint === currentSession.fingerprint
      );
    };

    // Search newest-to-oldest so repeated Comprehends on the same note revert
    // the latest matching EMR write, not the oldest one.
    const undoEntry = [...emrUndoStack].reverse().find((entry) => sessionsMatch(entry?.session));
    const lastSnapshot = undoEntry?.questions || [];
    const session = undoEntry?.session || null;

    if (lastSnapshot.length === 0) {
      _setStatus('No EMR changes to revert for this note.');
      return;
    }
    _setStatus('Reverting EMR...');
    const currentScan = await getEMRQuestionsWithCache(null, session);
    const currentQuestions = currentScan?.questions || [];
    if (session?.isDisposed?.()) {
      _setStatus('Could not verify the current EMR note to revert.');
      return;
    }
    console.log('Current questions:', currentQuestions);

    const answers = lastSnapshot
      .map((snap) => {
        const q = currentQuestions.find((q) => q.Key == snap.Key);
        console.log('snap:', snap);
        console.log('q:', q);
        if (!q) return null;
        if (q.Type === 'Flex' && snap.Structure) {
          // Flattening to get the existing values in string
          const flattened = flattenFlexStructure(snap.Structure, q.ParallelSections);
          console.log('Flattened Flex structure to send:', flattened);
          return { Question: q.Question, Answer: flattened };
        } else {
          return { Question: q.Question, Answer: snap.ExistingNote ?? '' };
        }
      })
      .filter(Boolean);

    console.log('JSON Answers to send:', JSON.stringify(answers, null, 2));

    if (answers.length === 0) {
      _setStatus('No matching EMR fields to revert.');
      return;
    }

    if (!session?.answerQuestionInEMR) {
      _setStatus('Could not verify the current EMR note to revert.');
      return;
    }

    for (const answer of answers) {
      await session.answerQuestionInEMR(answer.Question, answer.Answer, 'overwrite');
      if (session.isDisposed()) {
        _setStatus('Could not verify the current EMR note to revert.');
        return;
      }
    }
    setEmrUndoStack((stack) => stack.filter((entry) => entry !== undoEntry));
    _setStatus('Reverted to previous EMR text...');
  };

  // Vendor Apps: with the feature off (and no Dev Console override) none of the
  // effects below run — the store is never told about this visit and nothing is
  // written to app_context. Recomputed each render so a flag/dev-mode flip on a
  // profile refresh re-runs them.
  const vendorAppsOn = isVendorAppsEnabled(apiClient);

  // What this visit can open, in the clinician's order: pinned items are tabs
  // (and buttons on the compliance rail), the rest live in the Apps tab.
  const workspace = useVisitWorkspace({
    builtins: [
      'note',
      'chat',
      canShowCompliance && 'compliance',
      apiClient.features?.show_coding_tab && 'coding',
    ].filter(Boolean),
    vendorAppsOn,
    activeTab,
    setActiveTab,
    labels: apiClient.features?.scorecard ? { compliance: 'Scorecard' } : {},
  });
  // Vendor Apps (docs/vendor-apps-contract.md §4–5): this view IS the open
  // chart. Tell the store which patient/visit is open so vendor frames get
  // `comprehend:patient`, provides are checked against it, and the note gets
  // the right buffers. Cleared on unmount so nothing is "open" between visits.
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    // GUARANTEE: once this visit has a patient, the store (and every app frame)
    // gets one. On a fresh load straight into a visit, apiClient.patients can
    // arrive after this effect runs, so retry briefly; if the list still lacks
    // the record, fall back to what the visit itself knows (id + display name)
    // rather than telling apps "no chart open".
    let timer = null;
    let tries = 0;
    const attempt = () => {
      if (!groupId || groupId === -1) {
        vendorStore.setPatient(null, conversationID);
        return;
      }
      const record = apiClient.patients?.find((p) => String(p.uid) === String(groupId));
      if (record) {
        vendorStore.setPatient(patientFromRecord(record), conversationID);
        return;
      }
      if (tries++ < 20) {
        timer = setTimeout(attempt, 500);
        return;
      }
      vendorStore.setPatient({ id: String(groupId), name: group || '', dob: null }, conversationID);
    };
    attempt();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, apiClient, groupId, conversationID, apiClient?.patients?.length]);
  useEffect(() => () => vendorStore.setPatient(null, null), []);
  // The clinician clicked "Use <app>'s patient" on a visit with no patient: find
  // that person in the clinic's list by name + DOB, create them if absent, put
  // them on this visit. The patient effect above then runs and the store re-runs
  // whatever the app sent through the normal guard.
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    const assign = async ({ appId, name, dob }) => {
      try {
        const parts = String(name || '').trim().split(/\s*,\s*|\s+/).filter(Boolean);
        const [first, last] = name.includes(',') ? [parts[1] || '', parts[0] || ''] : [parts[0] || '', parts.slice(1).join(' ')];
        const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
        const sameDob = (a, b) => !a || !b || String(a).slice(0, 10) === String(b).slice(0, 10);
        let record = (apiClient.patients || []).find((r) => same(r.firstName, first) && same(r.lastName, last) && sameDob(r.dob, dob));
        if (!record) {
          const made = await apiClient.newPatient(first, last, dob || null);
          if (made?.error) throw new Error(made.error);
          const fresh = await apiClient.getUserGroups();
          if (fresh?.groups) apiClient.patients = fresh.groups;
          record = (apiClient.patients || []).find((r) => String(r.uid) === String(made.uid)) || { uid: made.uid, firstName: first, lastName: last, dob };
        }
        const rsp = await apiClient.editConversation(conversationID, 'group', record.uid);
        if (rsp?.error) throw new Error(rsp.error);
        __setGroup(getFullName(record));
        _setGroupId(record.uid);
        showToast.success(`${getFullName(record)} is now on this visit (from ${vendorStore.getApp(appId)?.name || 'the app'}).`);
      } catch (e) {
        showToast.error(`Couldn't pull that patient in: ${e.message}`);
      }
    };
    return vendorStore.on('assignPatient', assign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, conversationID]);
  // New transcript/note content invalidates cached subscription answers.
  useEffect(() => {
    if (vendorAppsOn && transcript) vendorStore.bumpContext();
  }, [vendorAppsOn, transcript]);

  const setTranscript = async (newTranscript) => {
    _setTranscript(newTranscript);
    await apiClient.editConversation(
      conversationID,
      'transcript',
      newTranscript,
    );
  };
  const setVisitNotes = (newVisitNotes) => {
    _setVisitNotes(newVisitNotes);
    apiClient.editConversation(conversationID, 'visit_notes', newVisitNotes);
  };
  // Dictation appends to whatever is already in Clinical Facts so several
  // dictations (and anything typed in between) stack up instead of overwriting
  // each other. Read through a ref because the dictation callback is created
  // once and would otherwise close over a stale value.
  const visitNotesRef = useRef(visitNotes);
  visitNotesRef.current = visitNotes;
  const appendVisitNotes = (text) => {
    const addition = (text || '').trim();
    if (!addition) {
      return;
    }
    const existing = (visitNotesRef.current || '').replace(/\s+$/, '');
    setVisitNotes(existing ? `${existing}\n${addition}` : addition);
  };
  // Vendor Apps (docs/vendor-apps-contract.md §5): what connected apps set as
  // context for the open patient is saved on the visit as its own field,
  // app_context, keyed by app id; the backend folds it into every prompt after
  // the clinician's own notes. Updated whenever an app provides or clears.
  // Working copy that is updated synchronously: several apps provide within the
  // same tick when a visit opens, and React state only catches up on re-render.
  // Without this, each event merged into a stale map and the saves overwrote
  // each other — you got one app's data or the other.
  const appContextRef = useRef(appContext);
  useEffect(() => {
    appContextRef.current = appContext;
  }, [appContext]);
  // Saves are coalesced: a short grace period lets every app land, then ONE
  // write carries the whole map. Flushed on leave so nothing is lost.
  // The pending save carries the map it will write and the visit it belongs to,
  // so a flush always writes that pair — never the current ref against an older
  // visit id (which could put visit B's app data on visit A's record).
  const appContextSaveRef = useRef({ timer: null, convo: null, value: null });
  const flushAppContext = () => {
    const pending = appContextSaveRef.current;
    if (!pending.timer) return;
    clearTimeout(pending.timer);
    pending.timer = null;
    apiClient.editConversation(pending.convo, 'app_context', pending.value || {});
  };
  const setAppContext = (next) => {
    const pending = appContextSaveRef.current;
    if (pending.timer && pending.convo !== conversationID) flushAppContext(); // switching visits: save the old one, with its own map, now
    appContextRef.current = next;
    _setAppContext(next);
    clearTimeout(pending.timer);
    pending.convo = conversationID;
    pending.value = next;
    pending.timer = setTimeout(() => {
      pending.timer = null;
      apiClient.editConversation(pending.convo, 'app_context', pending.value || {});
    }, 700);
  };
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    const sync = ({ appId, patientId }) => {
      if (!conversationID || !vendorStore.patient || patientId !== vendorStore.patient.id) return;
      const live = vendorStore.buffersForOpenPatient().find((b) => b.appId === appId);
      const next = { ...(appContextRef.current || {}) };
      if (live) next[appId] = { ...(next[appId] || {}), name: live.app, markdown: live.markdown, providedAt: live.providedAt, edited: false };
      else delete next[appId];
      if (JSON.stringify(next) !== JSON.stringify(appContextRef.current || {})) setAppContext(next);
    };
    // Answers we computed for an app are kept too — by structure hash, latest only —
    // so the note-tab rows show them after a reload and there is a record of what
    // each app was told for this visit.
    const syncAnswers = ({ appId, hash, payload }) => {
      if (!conversationID || !vendorStore.patient || !hash || payload?.patientId !== vendorStore.patient.id) return;
      const cur = appContextRef.current || {};
      const entry = cur[appId] || { name: vendorStore.getApp(appId)?.name || 'app', markdown: '', providedAt: null, edited: false };
      const answers = { ...(entry.answers || {}), [hash]: { answers: payload.answers, answeredAt: payload.answeredAt, version: payload.contextVersion } };
      setAppContext({ ...cur, [appId]: { ...entry, answers } });
    };
    const off = vendorStore.on('buffer', sync);
    const offAnswers = vendorStore.on('answers', syncAnswers);
    return () => {
      off();
      offAnswers();
      flushAppContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, conversationID]);
  const setTemplate = (
    newTemplate,
    configIdLocal,
    templateArg,
    samplesArg,
    templateReplyArg,
    extraBulkEdits = [],
  ) => {
    var templateLocal = templateArg ? templateArg : template;

    // Do a delta between the old and new questions as the API wants just the changed section
    configIdLocal = configIdLocal ? configIdLocal : configID;
    var bulkEdit = [];
    for (const key in newTemplate) {
      // Handle add_question when it's new
      if (!(key in templateLocal)) {
        bulkEdit.push({
          fieldName: 'add_question',
          key: key,
          value: newTemplate[key],
        });
      } else if (templateLocal[key] !== newTemplate[key]) {
        // Check if the old and new instructions are different
        if (
          !checkSectionAndSubsectionInstructionChange(
            templateLocal[key],
            newTemplate[key],
            bulkEdit,
            key,
          )
        ) {
          // Fallback: push default full save
          bulkEdit.push({
            fieldName: 'update_question_value',
            key,
            value: newTemplate[key],
          });
        }
      }
    }

    // Anything that comes in samples is going to be a new sample
    for (const key in samplesArg) {
      bulkEdit.push({
        fieldName: 'samples',
        key: key,
        value: samplesArg[key],
      });
    }

    // everything in the templateReply is going to be a new reply type
    for (const key in templateReplyArg) {
      bulkEdit.push({
        fieldName: 'replyType',
        key: key,
        value: templateReplyArg[key],
      });
    }
    bulkEdit.push(...extraBulkEdits);

    // console.log(bulkEdit);
    if (bulkEdit.length > 0) {
      apiClient
        .editConfigBulk(configIdLocal, bulkEdit)
        .then((data) => {
          if ('error' in data) {
            console.error(
              `Error editing template - configID = ${configIdLocal}`,
            );
            console.error(data);
            return;
          }
        })
        .catch((error) => {
          console.error(`Unexpected Error from template edit - ${error}`);
        });
    }
    _setTemplate(newTemplate);
  };

  // Say the user hit's New, the view does not change, but we need to reset the variables while we
  // wait for the api call to finish creating the new conversation
  useEffect(() => {
    // Reset the state variables when the hash changes...
    _setName(defaultName);
    _setStatus(defaultStatus);
    _setConfig(defaultConfig);
    _setGroup(defaultGroup);
    _setSecondsTranscribed(defaultSecondsTranscribed);
    _setTranscript(defaultTranscript);
    _setTemplate(defaultTemplate);
    _setSamples(defaultSamples);
    _setSubsections(defaultSubsections);
    setCodingRecommendations([]);
    setCodingPayerName(null);
    setCodingAutoRun(null);
    lastScrapedQuestionsRef.current = null;
    if (hash === '') {
      console.error('No conversation id specified');
      navigate('/');
      return;
    }
    setConversationID(parseInt(hash, 10));
  }, [hash]);

  /* Functions operating the view*/
  useEffect(() => {
    // Get the conversation details
    if (isInitialLoad === false) {
      loadTemplate(true, null, conversationID);
    }
  }, [conversationID]);

  /* Functions operating the view*/
  useEffect(() => {
    // Get the conversation details
    updateConversationDetails();

    // console.log("Refreshing Conversation Details")
    // Go get the conversation details for this conversation
    // TODO: Refresh the Template??
  }, [apiClient, conversationID, refresh]);

  /**
   * A deleted (Inactive) visit is nothing to work in: whatever opened it (an
   * old link, a stale panel, another tab) gets a fresh visit instead, as if
   * New Visit were pressed, on the tab that was asked for. The new visit picks
   * up the patient from the chart like any other. If one can't be made (no
   * subscription, a coordinator), say the visit was deleted rather than show it.
   */
  async function replaceDeletedVisit(deletedID) {
    if (replacedVisitRef.current === deletedID) return;
    replacedVisitRef.current = deletedID;
    let created = null;
    try {
      created = await apiClient.newConversation('');
    } catch (error) {
      console.error('Could not replace a deleted visit', error);
    }
    if (created?.uid) {
      navigate('/User/conversations/details#' + created.uid, {
        replace: true,
        state: landingTabRef.current ? { landingTab: landingTabRef.current } : undefined
      });
      return;
    }
    _setStatus('This visit was deleted. Start a New Visit to continue.');
  }

  async function updateConversationDetails() {
    // Handle the case where the view just loaded and we have not yet grabbed the conversationID from the hash...
    let localConvoID = conversationID;
    if (localConvoID === defaultConversationID) {
      localConvoID = parseInt(hashRef.current, 10);

      // Check if localConvoID is valid
      if (isNaN(localConvoID) || localConvoID <= 0) {
        console.error('Invalid localConvoID:', localConvoID);
        return;
      }

      setConversationID(localConvoID);
    }

    try {
      var data = await apiClient.getConversationDetails(localConvoID);
    } catch (error) {
      console.error('Error getting conversation details');
      console.error(error);
      return;
    }
    // console.log(data)
    if ('error' in data) {
      console.error('Error getting conversation details');
      _setStatus(data.error);
      return;
    }

    // Verify we are looking at the right conversation and something has not changed since we started
    // Protection against a race condition and asynchronous calls...
    // NOTE: use localConvoID (not conversationID) here — when called from the Recorder's stale
    // useEffect closure, conversationID is the mount-time value (-1) while localConvoID was
    // already resolved to the real ID from the URL hash above.
    var realHash = hashRef.current;
    if (localConvoID === parseInt(realHash, 10) && data.status === 'Inactive') {
      replaceDeletedVisit(localConvoID);
      return;
    }
    if (localConvoID === parseInt(realHash, 10)) {
      // only set the values if the field is not null or undefined
      _setName(data.name || defaultName);
      // Only set the status if this is the first time the page has loaded
      if (status === defaultStatus) {
        _setStatus(data.status || defaultStatus);
      }

      _setStatus(data.status || defaultStatus); // We are using this to show that the transcript has completed

      _setGroupId(data.group || defaultGroupId);
      // group is the uid of the patient, we are gonna do the lookup rather than store the uid
      // _setGroup(data.group || defaultGroup);
      _setTranscript(data.transcript || defaultTranscript);
      _setVisitNotes(data.visit_notes || defaultVisitNotes);
      // Connected apps' context for this visit (its own field, keyed by app id).
      _setAppContext(data.app_context || {});
      _setSecondsTranscribed(data.seconds || defaultSecondsTranscribed);
      // Set Compliance score
      _setComplianceScore(data.score || 0); // Default to 0 if undefined

      // A visit with a grade but nothing captured for it was scored from the
      // compliance rail, so the Note tab has nothing to show — open on the
      // criteria they actually came here to read.
      //
      // "No audio" alone is the wrong test: a clinician who types visit notes
      // instead of recording also ends up with seconds = 0, and their Note tab
      // is full. Require that nothing was captured at all.
      if (!landingTabChosenRef.current) {
        landingTabChosenRef.current = true;
        const nothingCaptured =
          !data.seconds && !data.transcript && !data.visit_notes;
        if (data.score && nothingCaptured) {
          // Through the landing ref, so the first-tab default below can't
          // override it (a tab the rail asked for still wins).
          landingTabRef.current = landingTabRef.current || 'compliance';
          setActiveTab('compliance');
        }
      }
      // Prefer requirement_evaluations if available, otherwise fall back to feedback
      const feedbackToUse = data.requirement_evaluations || data.feedback || [];
      _setComplianceFeedback(feedbackToUse);

      // if this API call comes after we get the list of patients, do the filter to find the patient name
      var patientName = apiClient.patients.filter(
        (patient) => patient.uid === data.group,
      )[0];
      if (patientName) {
        _setGroup(getFullName(patientName));
      } else {
        // else just set the default and the API call to get the list of patients will take care of this
        _setGroup(defaultGroup);
        setTriggerPatients(triggerPatients + 1);
      }

      // TODO this will cause the config to always be the one selected based on the EMR??? TODO figure this out...
      // _setConfig(data.config || defaultConfig);
      // _setConfigID(data.configId || defaultConfigID);
      setCodingRecommendations(data.coding_recommendations || []);
      setCodingPayerName(data.coding_recommendations_payer_name || null);
    }
  }

  const updateTemplate = (key, newAnswer) => {
    /**
     * Description:
     *     This function is called when the user updates a field in the template
     * Args:
     *    key: The key of the field being updated
     *    newAnswer: The new value of the field
     */
    const newTemplate = { ...template };
    newTemplate[key] = newAnswer;

    // This will update the local copy and the server
    setTemplate(newTemplate);

    // Update the filtered template if this key exists there
    if (key in templateFiltered) {
      const newTemplateFiltered = { ...templateFiltered };
      newTemplateFiltered[key] = newAnswer;
      _setTemplateFiltered(newTemplateFiltered);
    }
  };

  // If we have to make an API Call to get the configs and groups, we can do that here
  const [triggerPatients, setTriggerPatients] = useState(0);
  const [codingRecommendations, setCodingRecommendations] = useState([]);
  const [codingPayerName, setCodingPayerName] = useState(null);
  const [codingAutoRun, setCodingAutoRun] = useState(null);
  const tabsScrollRef = useRef(null);
  const [showTabLeft, setShowTabLeft] = useState(false);
  const [showTabRight, setShowTabRight] = useState(false);
  const touchStartXRef = useRef(0);
  const isTouchingRef = useRef(false);
  const tabsNavRef = useRef(null); // .nav-tabs element
  const [tabsBarH, setTabsBarH] = useState(0);

  useEffect(() => {
    // set the group given the patientID
    var patientName = apiClient.patients.filter(
      (patient) => patient.uid === groupId,
    )[0];
    if (patientName) {
      _setGroup(getFullName(patientName));
    }
  }, [group, groupId, uniqueGroups]);

  useEffect(() => {
    apiClient
      .updatePatients()
      .then((data) => {
        // set the unique groups to be the list of patients and defaultGroups
        // just grab the fullNames from the paitents, not ethe actual objects
        var patientNames = apiClient.patients.map((patient) =>
          getFullName(patient),
        );
        setUniqueGroups([...new Set([...uniqueGroups, ...patientNames])]);

        // set the group given the patientID
        var patientName = apiClient.patients.filter(
          (patient) => patient.uid === groupId,
        )[0];
        if (patientName) {
          _setGroup(getFullName(patientName));
        }
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.error('Did not get patients when calling update');
        console.error(error);
      });
    // We don't want them thinking they can change their template
  }, [triggerPatients]);

  function newConfig() {
    apiClient
      .newTemplate()
      .then((data) => {
        // console.log(data);
        navigate(`/User/templates/manager#${data['configId']}`);
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.error('Did not create new template');
      });
  }

  /**
   * Sets the patient from EMR info if the patient is currently unknown (groupId is empty/null/-1/0)
   * This function is called when comprehending to ensure the patient is set before processing
   */
  async function setPatient(session = null) {
    // Check if patient is unknown
    if (groupId && groupId !== -1 && groupId !== 0) {
      return; // Patient is already set, no need to do anything
    }

    try {
      const patientInfo = session
        ? await session.getPatientInfo()
        : await getPatientInfo();
      if (
        patientInfo &&
        patientInfo['First Name'] &&
        patientInfo['Last Name']
      ) {
        // Format DOB properly (from M/D/YYYY to YYYY-MM-DD)
        let dob = new Date(patientInfo['DOB']);
        dob = dob.toISOString().split('T')[0]; // Extracts the date part from the ISO string
        const patientResponse = await apiClient.patientEMRQuery(
          patientInfo['First Name'],
          patientInfo['Last Name'],
          dob,
          patientInfo['EMR ID'],
          patientInfo['Metadata'] || '',
        );
        // The server created a new patient if we needed, in all cases it returned the patient ID
        if (patientResponse && patientResponse['uid']) {
          // Update patients list to include the new/existing patient
          await apiClient.updatePatients();

          // Find the patient in the updated list
          const patient = apiClient.patients.filter(
            (p) => p.uid === patientResponse['uid'],
          )[0];

          if (patient) {
            const formattedName = getFullName(patient);
            // Add to unique groups if not already there
            setUniqueGroups([...new Set([...uniqueGroups, formattedName])]);
            __setGroup(formattedName);
            _setGroupId(patientResponse['uid']);

            // Update the conversation with the patient ID
            if (conversationID) {
              await apiClient.editConversation(
                conversationID,
                'group',
                patientResponse['uid'],
              );
            }
          }
        }
      }
    } catch (error) {
      // If there is an error with the injector or patient query, log it but don't throw
      console.error('Error getting patient info or querying patient:', error);
    }
  }

  /**
   * the visit already has a patient, so confirm the chart open in the
   * EMR belongs to that same patient before we write anything into it.
   *
   * The comparison is on EMR ID alone. Note that several injectors (the
   * Raintree family, Fusion, PracticePro, PracticePerfect, CoreHealth, Heno,
   * IntakeQ, Emerge, PTEverywhere, Therabill) synthesize "EMR ID" from the
   * patient's rendered name rather than reading a real identifier, so on those
   * EMRs this is effectively a name comparison - hence the case/whitespace
   * normalization before comparing.
   *
   * Returns null when the patients match or when there is nothing to compare,
   * otherwise the details needed for the warning popup.
   */
  async function checkPatientMatch(session = null) {
    // Log why we declined to warn, so a missing popup is debuggable.
    const skip = (reason, extra = {}) => {
      console.log('No patient warning:', reason, extra);
      return null;
    };

    // No patient on the visit yet - setPatient() covers that case.
    if (!groupId || groupId === -1 || groupId === 0)
      return skip('visit has no patient', { groupId });

    try {
      // uid and group have been both numbers and strings.
      const sameId = (a, b) => String(a) === String(b);
      let visitPatient = apiClient.patients?.find((p) => sameId(p.uid, groupId));

      // Patient list is cached 5 min; refetch past it when emrId is absent.
      if (!visitPatient || visitPatient.emrId === undefined) {
        cacheManager.remove('user_groups');
        await apiClient.updatePatients();
        visitPatient = apiClient.patients?.find((p) =>
          sameId(p.uid, groupId),
        );
      }

      if (!visitPatient)
        return skip('visit patient is not in the patient list', {
          groupId,
          knownUids: apiClient.patients?.map((p) => p.uid),
        });

      const patientInfo = session
        ? await session.getPatientInfo()
        : await getPatientInfo();

      const norm = (value) => String(value ?? '').trim().toLowerCase();
      const visit = {
        emrId: norm(visitPatient.emrId),
        first: norm(visitPatient.firstName),
        last: norm(visitPatient.lastName),
      };
      const emr = {
        emrId: norm(patientInfo?.['EMR ID']),
        first: norm(patientInfo?.['First Name']),
        last: norm(patientInfo?.['Last Name']),
      };
      console.log('Comparing patient', { visit, emr });

      // Warn only when nothing lines up. Names are compared crossed too,
      // since EMRs disagree on which field holds which name.
      const matched =
        (visit.emrId && visit.emrId === emr.emrId) ||
        (visit.first && (visit.first === emr.first || visit.first === emr.last)) ||
        (visit.last && (visit.last === emr.last || visit.last === emr.first));
      if (matched) return skip('patient matched', { visit, emr });

      if (!emr.emrId && !emr.first && !emr.last)
        return skip('the EMR page gave us no patient', { patientInfo });

      return {
        visitPatientName: getFullName(visitPatient),
        emrPatientName: [
          patientInfo?.['First Name'],
          patientInfo?.['Last Name'],
        ]
          .filter(Boolean)
          .join(' ')
          .trim(),
      };
    } catch (error) {
      // A failure in the check itself must never block comprehending.
      console.error('Error checking patient match:', error);
      return null;
    }
  }

  function newPatient(first, last, dob) {
    // Create the New Patient
    // add the patient to the list of patients
    var formattedName = getFullName({ firstName: first, lastName: last });
    uniqueGroups.push(formattedName);
    __setGroup(formattedName);

    apiClient.newPatient(first, last, dob).then((data) => {
      // Get the UID for the new patient
      var patientId = data['uid'];
      // Assign the patient to the conversation
      apiClient.updatePatients().then((data) => {
        var patient = apiClient.patients.filter(
          (patient) => patient.uid === patientId,
        )[0];
        setGroup(getFullName(patient));
        setTriggerPatients(triggerPatients + 1);
      });
    });
  }

  var answers = [
    // WebPT
    // {"Question": "Create Plan of Care Document:", "Answer": "True"},
    // {"Question": "Frequency:", "Answer": "2 times a week"},
    // {"Question": "Procedures", "Answer": ['Therapeutic Exercises', 'Therapeutic Activity', 'Gait Training', 'Neuromuscular Rehabilitation']},

    // MW Therapy
    {
      Question:
        'Patient Information Injury Date (ex. Quick Text,January,February,March,April,May,June,July,August,September,October,November,December,Clear the text)',
      Answer: 'February 1, 2023',
    },
    {
      Question:
        'Patient Information Surgery Date (ex. Quick Text,January,February,March,April,May,June,July,August,September,October,November,December,Clear the text)',
      Answer: 'February, 2023',
    },
    { Question: 'Subjective Comments:', Answer: 'Something subjective!!' },

    // Jane Template
    // {"Question": "Diagnosis & ICD 10","Answer": "Lateral epicondylitis, ICD-10 code M77.1"},
    // {"Question": "SUBJECTIVE\nHistory of Present Condition/Mechanism of Injury","Answer": "Started a few months ago, potentially caused by repetitive motion from playing tennis."},
    // {"Question": "SUBJECTIVE\nPrimary Concern/Chief Complaint","Answer": "Sharp pain on the outside of the elbow, especially when gripping something or twisting the wrist."},
    // {"Question": "Pain\nPain at Worst","Answer": "7"},
    // {"Question": "Pain\nPain at Best","Answer": "2"},
    // {"Question": "Pain\nCurrent Pain","Answer": "4"},
    // {"Question": "Pain\nPain Description","Answer": "Sharp, intermittent pain, mostly when making certain movements."},
    // {"Question": "Patient Goals","Answer": "To play tennis without pain and regain full use of the arm."},
    // {"Question": "Range of Motion\n\n\nTemplate: PROM           Right          LeftFlexionExtensoinAbductionERIRAROM    FlexionExtensionAbductionERIR","Answer": "PROM (Passive Range of Motion) for right arm includes abduction, external rotation (ER), internal rotation (IR); AROM (Active Range of Motion) includes flexion, extension, abduction, ER, IR."},
    // {"Question": "Goals\nShort Term Goal\n\nTemplate: (3 weeks)1 . Patient will demonstrate understanding and compliance with HEP with min A for support of therapy services. 2. Patient will report a reduction in pain by at least 3 points on NPRS. 3. Patient will demonstrates an improvement in ROM by at least 10 degrees4. Patient will demonstrate an increase in strength by at least 1/2 grade"        ,"Answer": "Reduce pain level to 2 or less during daily activities and improve grip strength."},
    // {"Question": "Goals\nLong Term Goal\n\nTemplate: (6 weeks)1. Patient will demonstrate independence with performance of HEP for support of therapy services. 2. Patient will return to"        ,"Answer": "Return to playing tennis pain-free and complete therapy successfully."},
    // {"Question": "Goals\nPatient Education","Answer": "Yes"},
    // {"Question": "Plan of Care\nTimes per week","Answer": "2x/week"},
    // {"Question": "Plan of Care\nNumber of weeks","Answer": "6 weeks"}

    // Prompt Template
    // {"Question": "Subjective", "Answer":"Subjective: Ms. Thompson presented today with complaints of shoulder discomfort. She described the pain as acute and lancinating, especially during abduction and overhead movements. The pain has been significantly impairing her activities of daily living, particularly reaching for objects at high levels. Ms. Thompson expressed frustration regarding her shoulder pathology and her strong desire for resolution. She reported experiencing tightness during the horizontal adduction stretch and difficulty with the external rotation exercises. Ms. Thompson also mentioned that the pain has been bothersome and conveyed her determination to optimize her condition. She was receptive to the proposed treatment plan and expressed optimism for a favorable prognosis."},
    // {"Question": "Objective", "Answer":"Objective:\n        - Patient presenting with glenohumeral pain and discomfort\n        - Patient reports sharp, lancinating pain with abduction and elevation of the arm\n        - Patient experiences difficulty performing tasks requiring high-reaching movements\n        - Range of motion exercises, including pendulum exercises, were performed\n        - Patient reported mild discomfort during circumduction of the shoulder\n        - Cross-body adduction stretch was performed to improve glenohumeral flexibility\n        - Patient experienced tightness during the stretch, indicating an effective stretch\n        - Strengthening exercises utilizing a resistance band for external rotation were performed\n        - Patient found the exercises challenging but effective in activating the targeted muscles\n        - Recommendation to improve postural alignment and body mechanics to prevent further strain on the glenohumeral joint\n        - Cryotherapy advised for reducing inflammation and managing pain\n        - Gentle glenohumeral stretches and self-myofascial release techniques were demonstrated for muscle tension relief\n        - Patient expresses motivation to enhance glenohumeral function and alleviate pain\n        - Patient instructed to maintain consistency with prescribed exercises and techniques at home\n        - Follow-up appointment scheduled for the following week."},
    // {"Question": "Assessment", "Answer":"Assessment:\n        - Patient presents with acute, lancinating pain in the shoulder exacerbated by abduction and flexion, indicative of a rotator cuff pathology.\n        - Limited glenohumeral range of motion and capsular tightness observed upon examination.\n        - Prescribed therapeutic interventions include resistance band exercises for strengthening and gentle stretching protocols to enhance shoulder mobility and stability.\n        - Patient received education on correct scapulohumeral alignment and ergonomics to prevent further strain.\n        - Pain management and inflammation reduction strategies include cryotherapy, controlled shoulder stretches, and self-administered soft tissue mobilization."},
    // {"Question": "Plan", "Answer":"Plan:\n        - Implement rehabilitation protocol focused on increasing shoulder range of motion through pendulum exercises and gentle stretching.\n        - Integrate cross-body adduction stretch to enhance shoulder flexibility.\n        - Perform external rotation exercises with resistance band to fortify rotator cuff musculature.\n        - Educate patient on proper shoulder positioning techniques and body mechanics to mitigate strain on the shoulder.\n        - Advise application of cryotherapy in the form of ice packs to the shoulder for 15-20 minutes at regular intervals to address inflammation and manage pain.\n        - Demonstrate gentle shoulder stretches and self-myofascial release techniques to alleviate muscular tension and facilitate relaxation.\n        - Emphasize consistency in home exercise regimen and techniques.\n        - Schedule follow-up appointment for next week."},
    // {"Question": "Contraindications to Therapy", "Answer":"None"},
    // // Patient Education
    // {"Question": "Patient Education", "Answer":"✔"},
    // {"Question": "Possible CPT Codes Treated", "Answer":
    //     [
    //         "97110  THERAPEUTIC EXERCISES to develop strength and endurance, range of motion, and flexibility",
    //         "97163  Physical therapy evaluation: high complexity"
    //     ]},
    // {"Question": "Possible Modalities Treated", "Answer":["Pain relief", "Diathermy", "Paraffin bath"]},
    // {"Question": "Rehab Prognosis/Potential", "Answer":"Excellent"},
    // // {"Question": "Patient Education", "Answer":""},

    // Athletico Template
    // {"Question": 'Date of onset', "Answer":"2023-10-19T16:08:53.020988"},
    // {"Question": 'Precautions', "Answer":["Seizures", "Latex Allergy"]},

    // Jake EMR Template
    // {"Question": 'What is the injury of the patient?', "Answer":"Sprain"},
    // {"Question": 'Where is the Injury?', "Answer":"Shoulder"},
    // {"Question": 'What side is the injury?', "Answer":"Left"},
    // {"Question": 'When did the pain start? (Format: Number of weeks ago)', "Answer":"2"},
    // {"Question": 'What did the physical therapist ask the patient to do?', "Answer":"Stretch"},
    // {"Question": 'What instruction did the physical therapist give the patient to do at home?', "Answer":"Stretch"},
    // {"Question": 'What type of pain did the patient feel?', "Answer":"Sharp"},
    // {"Question": "On a scale of 0 to 10, what was the patient's pain level?", "Answer":"7"},
    // {"Question": 'How far could the patient move their injured body part?', "Answer":"Not Far"},
    // {"Question": 'How often should the patient come to PT? (Format: number of times per week)', "Answer":"3"},

    // {"Question": 'What did the patient do to injure themselves?', "Answer":"Fell"},
    // {"Question": 'How long should the patient hold their stretch? (Format: number of seconds)', "Answer":"10"},
    // {"Question": 'How long should a patient expect PT to last? (Format: number of weeks)', "Answer":"6"}
  ];

  /** Load the template when the view loads */
  useEffect(() => {
    // Function to load the template
    if (isInitialLoad && !libraryConfigId) {
      loadTemplate(true);
    } else if (isInitialLoad && libraryConfigId) {
      loadTemplate(false, libraryConfigId, parseInt(hash, 10));
    }
  }, []);

  /* loadTemplate function loads a template for a given conversation or note type:
  1. Determines the local conversation ID from the URL if needed.
  2. Fetches page info and template config from the EMR system.
  3. On the first pass or when not showing all templates, queries for EMR questions and retries fetching the template config.
  4. On finding a valid config, fetches details and updates the state.
  5. If no valid config is found, prompts the user to choose between Auto-Generate or PDF Builder.
  6. If a closest match is found, prompts for Auto-Generate, PDF Builder, or Copy Instructions.
  7. If Auto-Generate is selected, skips the template builder and proceeds with scraping and auto-generating the template.
  8. If PDF Builder is selected, proceeds with PDF Builder with config ID only for full access users.
  9. If Copy Instructions is selected, copies from the closest match config (only if user has full access).
  10. If not the first pass, proceeds directly with scraping and auto-generating the template.

  CASE1: `newConvo` and `initialLoad` are both `true`
  This occurs on the first page load when the user hit the "New Conversation" button outside the conversation details view.
  In this case, the `conversationID` is not set yet `loadTemplate` is called here to prepare the view.
  Function Call: loadTemplate(newConvo=true, configId=null, conversationId = null)
      Case 1a: When the EMR is loaded but the note type is not open (e.g., as seen in Jane EMR),
      `pageInfo` and `configId` will be null. In such scenarios, the `loadTemplate` function will
      simply return without proceeding further. 

      This behavior occurs because there is no flag to indicate whether the note type is loaded.
      As a result, we only display the patient name and transcript without loading the template.

  CASE2: When `newConvo` is `true` and `initialLoad` is `false`
  This happens when the user creates a new conversation by clicking the "+" button
  while already in the conversation view. At this point, `conversationID`is set.
  Function Call: loadTemplate(newConvo=true, configId=null, conversationId = conversationID)

  CASE3: When `newConvo` and `initialLoad` are both `false`
  This occurs when the user is already in a conversation view and performs actions such as:
  - Clicking "Scan" -> loadTemplate(newConvo=false, configId=null, conversationId = null)
  - Clicking "Comprehend" -> loadTemplate(newConvo=false, configId=null, conversationId = null)
  - Clicking "Copy Instructions" -> loadTemplate(newConvo=false, configId=configId, conversationId = null)
  - Changing the configuration from the picker -> loadTemplate(newConvo=false, configId=configId, conversationId = null)
  `loadTemplate` is called to update the view with changes based on the action performed.

  CASE4: When we have libraryConfigId and `initialLoad` is `true`
  This occurs when new conversation created from the Template Library.
  Function Call: loadTemplate(newConvo=false, configId=libraryConfigId, conversationId = conversationID)
  */
  async function loadTemplate(
    newConvo = false,
    configIdArg = null,
    conversationIdArg = null,
    session = null,
    replyTypeOverridesArg = null,
  ) {
    let configIDLocal = configIdArg || configID;
    let localConvoID = conversationIdArg || conversationID;
    let replyTypeRevisionAtLoad = replyTypeRevisionsRef.current[configIDLocal] || 0;
    if (conversationID === defaultConversationID) {
      localConvoID = parseInt(hashRef.current, 10);
      setConversationID(localConvoID);
    }

    try {
      let templateConfig = {};
      let addReplyType = false;
      await apiClient.getAllConfigs();
      const pageInfo = session
        ? await session.getPageInfo()
        : await getPageInfo();
      console.log('pageInfo', pageInfo);
      if (!pageInfo || !pageInfo['URL'] || !pageInfo['Note Type']) {
        return;
      }

      let emrQuestions = [];
      if (newConvo || !configIDLocal || configIDLocal === -1) {
        console.log('newConvo or !configIDLocal or configIDLocal === -1');
        const { questions } = await getEMRQuestionsWithCache(null, session);
        emrQuestions = questions;
        const sections = emrQuestions.map((item) => item.Key);
        setBannerText('Loading Conversation Details...');
        setAiThinking(true);
        // Use the retry mechanism for templateEMRQuery as it times out in some cases
        templateConfig = await retryTemplateEMRQuery(
          pageInfo['URL'],
          pageInfo['Note Type'],
          sections,
          undefined,
          emrQuestions.map((item) => {
            // Lets the backend's rename matcher tell a View More field's old
            // flat section from a renamed section (COM-561). Built on a copy:
            // the scrape gets its real subSections later, in scrapeTemplate.
            const subSections = addFlexSubsections({ ...item }).subSections;
            return {
              key: item.Key,
              type: item.Type,
              ...(subSections
                ? { subsections: Object.keys(subSections) }
                : {}),
            };
          }),
        );

        setAiThinking(false);

        if (!templateConfig) {
          console.error(
            'Failed to retrieve template configuration after retries.',
          );
          _setStatus(
            'Failed to scan the page. Consider creating a new conversation.',
          );
          return;
        }
        setIsInitialLoad(false);

        configIDLocal = templateConfig['configId'] || configIDLocal;
        replyTypeRevisionAtLoad = replyTypeRevisionsRef.current[configIDLocal] || 0;
      }
      if (configIDLocal === null || configIDLocal === -1) {
        return;
      }
      let configDetails = await apiClient.getConfigDetails(configIDLocal);
      if (replyTypeOverridesArg) {
        configDetails = {
          ...configDetails,
          replyType: {
            ...configDetails.replyType,
            ...replyTypeOverridesArg,
          },
        };
      }
      await loadAllTemplates(pageInfo, configDetails?.name);
      await updateTemplateState(
        configDetails,
        configIDLocal,
        templateConfig,
        localConvoID,
        replyTypeRevisionAtLoad,
      );

      await fetchUserSharedConfigs(configIDLocal);
      // scrapeTemplate can auto-generate missing instructions from scraped EMR
      // fields, so Comprehend passes the session through this path as well.
      const scrapeResult = await scrapeTemplate(
        configDetails.questions,
        configIDLocal,
        configDetails.samples,
        configDetails.replyType,
        addReplyType,
        configDetails.subsections,
        configDetails.noteType,
        session,
        replyTypeRevisionAtLoad,
        configDetails.access_level === 'Full' && configDetails.subsectionNames
          ? configDetails.subsectionNames
          : null,
      );

      return {
        configId: configIDLocal,
        emrQuestions: scrapeResult.emrQuestions,
        configDetails: {
          ...configDetails,
          questions: scrapeResult.template,
          replyType: scrapeResult.templateReply,
          samples: scrapeResult.samples,
          subsections: scrapeResult.subsections,
        },
      };
    } catch (error) {
      console.error('Error loading template');
    }
  }

  // Update State After Loading Template
  const updateTemplateState = async (
    configDetails,
    configIDLocal,
    templateConfig,
    localConvoID,
    replyTypeRevisionAtLoad,
  ) => {
    templateConfig = { configId: configIDLocal };
    if (apiClient.showAllEMRTemplates) {
      const matchingConfig = apiClient.configs.find(
        (config) => config.configId === templateConfig?.configId,
      );
      setViewMode(matchingConfig ? matchingConfig.access_level : 'Full');
    }

    _setTemplate(configDetails.questions);
    _setSamples(configDetails.samples);
    _setSubsections(configDetails.subsections || {});
    if (
      replyTypeRevisionAtLoad ===
      (replyTypeRevisionsRef.current[configIDLocal] || 0)
    ) {
      templateReplyConfigIDRef.current = configIDLocal;
      _setTemplateReply(configDetails.replyType);
    }

    // Denote the template as shared if it is not owned by the user
    const templateName =
      configDetails.owner !== apiClient.email
        ? `${configDetails.name} (${configDetails.owner})`
        : configDetails.name;

    // setUniqueConfigs([...new Set([...uniqueConfigs, templateName])]);

    await setConfig(
      templateName,
      parseInt(localConvoID),
      parseInt(templateConfig['configId']),
    );
    _setConfigID(templateConfig['configId']);
  };

  // Extract reply type from top-level replyType and from questions with SubSections
  const extractReplyType = (configDetails) => {
    const TopLevelReplyType = { ...(configDetails?.replyType || {}) };
    const SubSectionsReplyType = {};
    const questions = configDetails?.questions || {};

    // Parse questions to extract replyType from SubSections
    Object.keys(questions).forEach((questionKey) => {
      const questionValue = questions[questionKey];
      let parsed = {};

      // Try to parse if it's a JSON string
      if (typeof questionValue === 'string') {
        try {
          parsed = JSON.parse(questionValue) || {};
        } catch {
          // Not a JSON string, skip
          return;
        }
      } else if (typeof questionValue === 'object' && questionValue !== null) {
        parsed = questionValue || {};
      } else {
        return;
      }

      // Check if it has SubSections with replyType
      if (
        parsed &&
        parsed.SubSections &&
        typeof parsed.SubSections === 'object'
      ) {
        Object.keys(parsed.SubSections).forEach((subsectionKey) => {
          const subsection = parsed.SubSections[subsectionKey];
          if (subsection && subsection.replyType) {
            // Format: "Section - Subsection": "replyType"
            const replyTypeKey = `${questionKey} - ${subsectionKey}`;
            SubSectionsReplyType[replyTypeKey] =
              subsection?.replyType || 'overwrite';
          }
        });
      }
    });
    return { TopLevelReplyType, SubSectionsReplyType };
  };

  // Special handling for emr.webpt.com URLs
  const getReplyTypeForWebPT2 = async (pageInfo, configIdLocal) => {
    // when template is changed manually we already have the configId, we need to get the reply type for the new template directly
    if (!configIdLocal || configIdLocal === -1) {
      // First call retryTemplateEMRQuery with empty sections
      const templateConfig = await retryTemplateEMRQuery(
        pageInfo['URL'],
        pageInfo['Note Type'],
        [],
        undefined,
        undefined,
        true,
      );

      if (templateConfig.replyTypeConfig) {
        return extractReplyType(templateConfig.replyTypeConfig);
      }

      configIdLocal = templateConfig['configId'];
      // When configId is missing, return null. This sets replyType to null during
      // getEmrQuestion(), which disables any skip logic. As a result, all sections
      // are processed with no exclusions.
      if (configIdLocal === null || configIdLocal === -1) {
        return null;
      }
    }

    // Get config details to retrieve reply type
    const configDetails = await apiClient.getConfigDetails(configIdLocal);

    const replyType = extractReplyType(configDetails);

    return replyType;
  };

  // Template Loading with Retry
  const retryTemplateEMRQuery = async (
    url,
    noteType,
    sections,
    retries = 12,
    sectionMetadata,
    replyTypesOnly,
  ) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const invalidSections = new Set(['none', 'null', 'undefined', '"', '']);

        sections = sections.filter(
          (section) =>
            !invalidSections.has(String(section).trim().toLowerCase()),
        );
        return await apiClient.templateEMRQuery(
          url,
          noteType,
          sections,
          sectionMetadata,
          replyTypesOnly,
        );
      } catch (error) {
        if (attempt < retries) {
          console.warn(`Retry ${attempt} failed. Retrying in 5 seconds...`);
          await new Promise((resolve) => setTimeout(resolve, 5000));
        } else {
          // setAiThinking(false); if we do this, we may inadvertency close the thinking banner while comprehending
          console.error('All retries for templateEMRQuery failed');
          return null;
        }
      }
    }
  };

  async function loadAllTemplates(pageInfo, currentTemplateName = null) {
    let configs = apiClient.configs;

    // console.log('All Configs ---> ', configs);
    // Get EMR templates matching the page's EMR URL
    let emr_configs = configs.filter((config) =>
      pageInfo['URL'].includes(config.emr_url),
    );
    const emrTemplateNames = emr_configs.map((config) => config.name);
    if (
      currentTemplateName &&
      !emrTemplateNames.includes(currentTemplateName)
    ) {
      emrTemplateNames.push(currentTemplateName);
    }
    // Format EMR templates with header and separators if they exist
    const formattedEMRTemplates =
      emrTemplateNames.length > 0
        ? [
            '---------------',
            '(EMR Notes)',
            '---------------',
            ...emrTemplateNames,
          ]
        : [];

    // Get Builder Templates (templates with hasTrainingData: true) and only those NOT in the EMR templates
    let builderTemplates = getBuilderTemplates(configs).filter(
      (template) => !emrTemplateNames.includes(template),
    ); // Exclude EMR templates

    // Construct uniqueConfigsNew from scratch
    const uniqueConfigsNew = [
      ...formattedEMRTemplates, // EMR templates with header
      ...(builderTemplates.length > 0
        ? [
            '---------------',
            '(Custom Notes)',
            '---------------',
            ...builderTemplates,
          ]
        : []), // Builder Templates with header, if any
    ];
    setUniqueConfigs(uniqueConfigsNew);
  }

  /**
   * Filters and groups templates that have training data.
   */
  function getBuilderTemplates(configs) {
    // Filter templates that have training data
    return configs
      .filter((config) => config.hasTrainingData === true)
      .map((config) => config.name);
  }

  /**
   * Parses the given list of configs and categorizes them into three types:
   * - EMR Templates (Configs that have a non-null `emr_url` and no `sharedConfigId`)
   * - Web Templates (Configs that have a null `emr_url` and no `sharedConfigId`)
   * - Shared Templates (Configs that have a `sharedConfigId`)
   */
  function groupTemplatesByType(configs) {
    let emrTemplates = configs.filter(
      (config) => config.emr_url !== null && !config.sharedConfigId,
    );
    let webTemplates = configs.filter(
      (config) => config.emr_url === null && !config.sharedConfigId,
    );
    let sharedTemplates = configs.filter(
      (config) => config.sharedConfigId != null,
    );

    // console.log('EMR Templates --> ', emrTemplates);
    // console.log('Web Templates --> ', webTemplates);
    // console.log('Shared Templates --> ', sharedTemplates);

    return [
      ...(emrTemplates.length > 0
        ? ['---------------', '(EMR Notes)', '---------------']
        : []),
      ...emrTemplates.map((config) => config.name),
      ...(webTemplates.length > 0
        ? ['---------------', '(Web Notes)', '---------------']
        : []),
      ...webTemplates.map((config) => config.name),
      ...(sharedTemplates.length > 0
        ? ['---------------', '(Shared Notes)', '---------------']
        : []),
      ...sharedTemplates.map(
        (config) => config.sharedConfigName || config.name,
      ),
    ];
  }

  // Seems like this might be duplicate logic??
  async function fetchUserSharedConfigs(id) {
    try {
      const email = apiClient.email;
      const should_batch = apiClient.isBatchingEnabled;
      setUserEmail(email);
      setQuestionBatching(should_batch);
      setExistingNote(apiClient.modifyExisting);
      const domain = email.split('@')[1];

      const sharedConfigsData = await apiClient.getUserSharedConfigs(
        '@' + domain,
      );
      if (!sharedConfigsData || !sharedConfigsData.sharedConfigs) {
        console.error('Error getting user shared configs');
        return Promise.reject('Error getting user shared configs');
      }
      const sharedConfigs = sharedConfigsData.sharedConfigs;
      const foundConfig = sharedConfigs.find(
        (config) => config.sharedConfigId === id,
      );
      if (foundConfig) {
        setViewMode(foundConfig.access_level);
      } else {
        setViewMode('Full');
      }
    } catch (error) {
      console.error('Error getting user shared configs:', error);
    }
  }

  /**
   * Build subsection placeholders for a Flex-type EMR question.
   *
   * Rules:
   * - Uses only the top-level section names (Patient Presentation, Past Medical History, Current Medications)
   * - If the question already has saved SubSections, merge new ones in.
   * - Updates the element.Question with the top-level instruction if present.
   */

  function addFlexSubsections(element) {
    if (element.Type !== 'Flex' || !element.Structure) {
      return element;
    }

    const subSections = {};

    // Recursively walk a structure object to find valid field nodes
    const processStructure = (structure, parentKey = null) => {
      const keys = Object.keys(structure);

      // If structure has just one object, do nesting part
      if (keys.length === 1) {
        Object.keys(structure).forEach((key) => {
          const value = structure[key];

          if (value && typeof value === 'object') {
            // If this object looks like a field node
            if (
              'Existing Value' in value ||
              'Choices' in value ||
              'Description' in value ||
              Object.keys(value).length === 0
            ) {
              subSections[key] = { Instruction: '', replyType: 'overwrite' };
            } else {
              // Nested structure — recurse
              processStructure(value, key);
            }
          } else if (
            value &&
            ('Existing Value' in structure ||
              'Choices' in structure ||
              'Description' in structure ||
              Object.keys(structure).length === 0)
          ) {
            subSections[key] = { Instruction: '', replyType: 'overwrite' };
          }
        });
      } else {
        // If more than one object, use original logic
        Object.keys(structure).forEach((sectionKey) => {
          // Make sure this section actually contains fields (not empty)
          const section = structure[sectionKey];
          if (
            section &&
            typeof section === 'object' &&
            Object.keys(section).length > 0
          ) {
            subSections[sectionKey] = {
              Instruction: '',
              replyType: 'overwrite',
            };
          }
        });
      }
    };

    // flex++ toggles sit beside the section root; drop them so a single-section
    // tree still yields its fields as subsections.
    const structureRoot =
      element.Structure['flex++'] === true
        ? Object.fromEntries(
            Object.entries(element.Structure).filter(
              ([k]) => k !== 'flex++' && k !== 'prune_early',
            ),
          )
        : element.Structure;
    processStructure(structureRoot);

    // Attach generated subsections
    element.subSections = subSections;

    // Merge in any saved SubSections from Question
    if (typeof element.Question === 'object' && element.Question?.SubSections) {
      Object.keys(subSections).forEach((subKey) => {
        if (element.Question.SubSections[subKey]) {
          subSections[subKey] = {
            Instruction: element.Question.SubSections[subKey].Instruction || '',
            replyType:
              element.Question.SubSections[subKey].replyType || 'overwrite',
          };
        }
      });

      // Carry over top-level instruction
      element.Question = element.Question.TopLevelInstruction || '';
    }

    return element;
  }

  function sendDropDownMissesToSlack(
    misses,
    configIdLocal,
    conversationID,
    noteType,
  ) {
    if (Object.keys(misses).length > 0) {
      const channelName = 'NOTIFICATION_WEBPT2_CACHE_MISSES';
      const userInfo = {
        name: apiClient.name,
        ConfigId: configIdLocal,
        NoteType: noteType,
        ConversationID: conversationID,
      };
      const description = `Questions processed for configId ${configIdLocal} for note type ${noteType} in conversation ${conversationID}. Misses: ${Object.keys(misses).length}\n\n`;
      const summary = misses;
      console.log('Summary --> ', summary);
      const jsonFile = {
        content: summary,
        filetype: 'json',
        filename: `misses_${configIdLocal}_${noteType}_${conversationID}.json`,
      };
      apiClient.sendSlackMessage(
        description,
        channelName,
        userInfo,
        null,
        jsonFile,
      );
    }
  }

  /** Scrape the Fields from the EMR
   *  Triggered by the Scrape Button and after the template loads
   *  - Adding sections to this template
   *  - Hiding sections that are not currently shown on the EMR
   *  - When session is provided, scrape from the Comprehend tab instead of the active tab
   * */
  async function scrapeTemplate(
    templateArg = null,
    configIdArg = null,
    samplesArg = null,
    templateReplyArg = null,
    addReplyType = false,
    subsectionsArg = null,
    noteType = null,
    session = null,
    replyTypeRevisionAtLoad,
    savedSubsectionNames = null,
  ) {
    // Because we are using state variable, we won't have updated the template yet, so we need to use the arg...
    // We will use configIdArg as the tell because templateArg has the potential to be the event object on the button click
    var templateLocal = templateArg ? templateArg : template;
    var configIdLocal = configIdArg ? configIdArg : configID;
    var samplesLocal = samplesArg ? samplesArg : samples;
    var samplesUpdate = {};
    var templateReplyLocal = templateReplyArg
      ? templateReplyArg
      : templateReply;
    var templateReplyUpdate = {};
    var subsectionsLocal = subsectionsArg ? subsectionsArg : subsections;
    var subsectionsUpdate = {};
    // Scrape the page and grab the fields currently being shown. Manual calls
    // use the active tab; Comprehend calls pass a session to pin this scrape.
    // TODO Filter the fields in the config to only show what is currently being shown
    const {
      questions: emrQuestions,
      dropdownMisses,
      emrURL,
    } = await getEMRQuestionsWithCache(configIdLocal, session);
    // console.log("Got EMR Questions")
    // console.log(emrQuestions)

    // Loop over the questions we scraped from the EMR and show them, creating sections as necessary
    var newTemplateFiltered = {};
    emrQuestions.forEach((element) => {
      if (!element.Key && !(element.Key in newTemplateFiltered)) {
        newTemplateFiltered[element.Key] = '';
        return;
      }

      addFlexSubsections(element);
    });

    // Duplicate the template and add the new question (This one get's sync'ed back to the server)
    const newTemplate = { ...templateLocal };

    //Filter questions that need AI-generated instructions
    const elementsToProcess = emrQuestions.filter(
      (element) =>
        element['Key'] && !(element['Key'] in templateLocal) && element['Type'],
    );
    const elementNotToProcess = emrQuestions.filter(
      (element) =>
        element['Key'] && element['Key'] in templateLocal && element['Type'],
    );

    if (elementsToProcess.length > 0) {
      setDisableComprehend(true);
      setBannerText('Generating Instructions...');
      setAiThinking(true);
    }

    // Generate AI instructions with context from existing and currently visible sections
    if (elementsToProcess.length > 0) {
      try {
        const allKnownSections = [
          ...new Set([
            ...Object.keys(templateLocal || {}),
            ...emrQuestions.map((element) => element['Key']).filter(Boolean),
          ]),
        ];
        const sectionsToGenerate = elementsToProcess.map((element) => ({
          key: element['Key'],
          primer: element['Question'],
          type: element['Type'],
          choices: element['Choices'],
          replyType: element['ReplyType'],
          // View More names, so the rename matcher never copies a field's old
          // flat section in as the whole section (COM-561).
          ...(element.subSections
            ? { subsections: Object.keys(element.subSections) }
            : {}),
        }));

        let newInstructions = await apiClient.genInstructionsInline(
          configIdLocal,
          sectionsToGenerate,
          allKnownSections,
          emrQuestions.map((element) => element['Key']).filter(Boolean),
        );
        if (newInstructions['error']) {
          throw new Error(newInstructions['error']);
        }
        newInstructions = newInstructions['newInstructions'] || {};

        for (const element of elementsToProcess) {
          const generatedInstruction = newInstructions[element['Key']];
          if (!generatedInstruction) {
            console.warn(`No instruction generated for ${element['Key']}`);
            continue;
          }
          // Keep element['Question'] as the scanned EMR field label — it's
          // still needed later to match PUSH_ANSWER against the injector's
          // DOM scan. The generated instruction only belongs in the template
          // maps (what gets saved/rendered as the section's instruction).
          newTemplate[element['Key']] = generatedInstruction;
          newTemplateFiltered[element['Key']] = generatedInstruction;
          if (element['ReplyType']) {
            templateReplyUpdate[element['Key']] = element['ReplyType'];
          }
          if (element['Samples']) {
            samplesUpdate[element['Key']] = element['Samples'];
          }
          if (element['Structure']) {
            // Use subSections directly since it's already a proper object
            const subSectionsObj = element['subSections'] || {};
            subsectionsUpdate[element['Key']] = JSON.stringify(subSectionsObj);
          }
        }
      } catch (err) {
        console.warn(`Error generating instructions: ${err.message}`);
      }
      setAiThinking(false);
      setDisableComprehend(false);
    }

    // Check if reply type or samples is in the scraped template
    elementNotToProcess.forEach((element) => {
      // Ensure existing questions are included
      if (element['Key'] in templateLocal) {
        // console.log(
        //   `${element['Key']} - Question already has a section in the template, no need to make a new one`
        // );
        newTemplateFiltered[element['Key']] = templateLocal[element['Key']];
        // If an initial reply type is sent, we need to add a reply type when creating a question for the first time.
        // Previously, the backend was setting it to 'overwrite' irrespective of the provided reply type.
        if (addReplyType && element['ReplyType']) {
          templateReplyUpdate[element['Key']] = element['ReplyType'];
        }
        if (element['subSections']) {
          // Use subSections directly since it's already a proper object
          const subSectionsObj = element['subSections'] || {};
          subsectionsUpdate[element['Key']] = JSON.stringify(subSectionsObj);
        }
      } else {
        console.log('Question not being added to the template');
        console.log(element);
      }
    });
    samplesLocal = { ...samplesLocal, ...samplesUpdate };
    templateReplyLocal = { ...templateReplyLocal, ...templateReplyUpdate };

    // COM-561: carry old flat-section instructions into View More.
    const integrationUpgrade = integrationUpgradeFor(apiClient, emrURL);
    if (integrationUpgrade) {
      const liveSectionKeys = new Set(emrQuestions.map((element) => element.Key));
      emrQuestions.forEach((element) => {
        const key = element['Key'];
        if (!element.subSections || !(key in newTemplate)) return;
        const migrated = withLegacySubsectionInstructions(
          newTemplate[key],
          Object.keys(element.subSections),
          newTemplate,
          templateReplyLocal,
          liveSectionKeys,
          integrationUpgrade.aliases,
        );
        if (migrated === newTemplate[key]) return;
        newTemplate[key] = migrated;
        newTemplateFiltered[key] = migrated;
      });
    }

    // Parse JSON strings in subsectionsUpdate back to objects
    const parsedSubsectionsUpdate = {};
    Object.keys(subsectionsUpdate).forEach((key) => {
      try {
        parsedSubsectionsUpdate[key] = JSON.parse(subsectionsUpdate[key]);
      } catch {
        parsedSubsectionsUpdate[key] = subsectionsUpdate[key];
      }
    });

    subsectionsLocal = { ...subsectionsLocal, ...parsedSubsectionsUpdate };
    // We set them here because if we tried to do it inside the for, we would overwrite it and only take the last one.
    _setSamples(samplesLocal);
    if (
      replyTypeRevisionAtLoad ===
      (replyTypeRevisionsRef.current[configIdLocal] || 0)
    ) {
      templateReplyConfigIDRef.current = configIdLocal;
      _setTemplateReply(templateReplyLocal);
    }
    _setTemplateFiltered(newTemplateFiltered);
    _setSubsections(subsectionsLocal);
    // Save newly seen "View More" subsection names so Note Settings can list
    // them. null = this user can't edit the template (or the API predates it).
    const subsectionNameEdits = savedSubsectionNames
      ? Object.entries(
          subsectionNamesToSave(
            emrQuestions.filter((element) => element['Key'] in newTemplate),
            savedSubsectionNames,
          ),
        ).map(([key, names]) => ({
          fieldName: 'subsectionNames',
          key,
          value: names,
        }))
      : [];
    try {
      await setTemplate(
        newTemplate,
        configIdLocal,
        templateLocal,
        samplesUpdate,
        templateReplyUpdate,
        subsectionNameEdits,
      );
    } catch (error) {
      console.error('Failed to set template:', error);
    }
    // Send misses to Slack if any exist
    sendDropDownMissesToSlack(
      dropdownMisses,
      configIdLocal,
      conversationID,
      noteType,
    );
    // Return the updated values needed for immediate use after calling scrapeTemplate
    return {
      emrQuestions,
      template: newTemplate,
      templateReply: templateReplyLocal,
      samples: samplesLocal,
      subsections: subsectionsLocal,
    };
  }

  function showRateLimitModal() {
    // Display the modal to the user
    setPopupVisible(true);
  }

  function handleRateLimitConfirmation() {
    setPopupVisible(false); // Hide modal
    setIsDowngraded(true); // Set downgraded mode
  }

  function hasNoneOrNullorTooBrief(answer) {
    if (answer === null || answer === undefined) {
      return true;
    }

    // Check for the "too brief" in answer pattern
    if (
      typeof answer === 'string' &&
      answer ===
        'Your input is too brief to generate an accurate note. Please provide more details, aiming for at least 25 words. The more detailed your description, the better the notes you will receive.'
    ) {
      return true;
    }

    if (typeof answer === 'string' && answer.toLowerCase().includes('none')) {
      return true;
    }

    // Check for "None" in a Flex-type response.
    // Since the answer may be a nested object, we recursively check all values.
    // If any value is "none" (case-insensitive), return true.
    if (typeof answer === 'object') {
      return Object.values(answer).some((section) => {
        if (section === null) return true;

        if (typeof section === 'string') {
          return section.toLowerCase().includes('none');
        }

        if (typeof section === 'object') {
          return Object.values(section).some(
            (val) =>
              val === null ||
              (typeof val === 'string' && val.toLowerCase().includes('none')),
          );
        }

        return false;
      });
    }

    return false;
  }

  // useEffect to detect changes in `isDowngraded` and retry comprehension
  useEffect(() => {
    if (isDowngraded) {
      comprehendTemplate(); // Retry comprehension with updated state
    }
  }, [isDowngraded]); // Run when `isDowngraded` changes

  async function comprehendTemplate(
    key,
    updateSectionStatusIndicator = null,
    {
      // set when continuing past the mismatch warning
      skipPatientMatchCheck = false,
      // what started this run, stored with its feedback rating (FEEDBACK_SOURCES)
      source = FEEDBACK_SOURCES.comprehend,
    } = {},
  ) {
    // Generating the WHOLE note is the clinician action that answers connected
    // apps (docs/vendor-apps-contract.md §3). Re-running one section — or the
    // sections an instruction change touched — is not; leave the apps alone.
    if (vendorAppsOn && (!key || (Array.isArray(key) && key.length === 0))) vendorStore.requestEvaluate('note');
    const transcriptIssue = checkTranscript(transcript, secondsTranscribed);
    if (transcriptIssue === 'not_recorded' || transcriptIssue === 'recording_error') {
      showToast.error(
        <div>
          <p className="mb-1">Sorry, your recording didn’t come through. Share a quick visit summary so Comprehend can fill out your note.</p>
          <p className="mb-0">Before recording again, make sure the right mic is selected and unmuted.</p>
        </div>,
      );
      return;
    }
    if (transcriptIssue === 'insufficient') {
      showToast.warning(
        'Comprehend needs a little more detail to write your note. Click "Resume" to add more about the visit, then click "Comprehend" again.',
      );
      return;
    }
    const replyTypeAtComprehend = { ...templateReply };
    // Pin every EMR read/write below to the tab the user clicked Comprehend
    // on, and capture a patient fingerprint so late-returning writes can't
    // land in a different patient's chart if the user navigates away.
    let session;
    try {
      session = await createEMRSession();
    } catch (err) {
      console.error('Failed to create EMR session:', err);
      showToast(
        '⚠️ Open up the patient note in your EMR to continue comprehending'
      );
      return;
    }
    const pageInfo = session.initialPageInfo;
    console.log('pageInfo', pageInfo);
    if (!pageInfo || !pageInfo['URL'] || !pageInfo['Note Type']) {
      showToast(
        '⚠️ Open up the patient note in your EMR to continue comprehending',
      );
      return;
    }
    console.log(`Attempting to comprehend template`);
    console.log(key);
    updateSectionStatusIndicator?.(key, 'pending');

    logGAEvent('comprehend_conversation', apiClient, {
      conversation_id: conversationID,
    });

    // Check user account allows this or not
    if (!apiClient.actionAllowed()) {
      _setStatus('Account is not activated.');
      updateSectionStatusIndicator?.(key, 'error');
      return false;
    }

    // Block comprehension while audio is still uploading or the user is actively editing.
    // window.onbeforeunload is set by Recorder whenever isRecording || isUploading, and also
    // when a transcript field edit is in flight. Give it 500 ms to clear (covers the edit case),
    // then hard-stop if the lock is still held — the final audio chunk likely hasn't finished
    // transcribing yet and the note would be generated from an incomplete transcript.
    if (window.onbeforeunload) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (window.onbeforeunload) {
        updateSectionStatusIndicator?.(key, 'pending');
        console.log("Window is locked, looks like you're editing");
        _setStatus('Please wait for the edit to finish before comprehending');
        return false;
      }
    }

    _setStatus('Comprehending...');
    setBannerText('Comprehending...');
    setAiThinking(true);
    // Any new run retires the previous run's feedback bar; this run shows its own
    // when it completes.
    setFeedbackRun(null);

    // Check if patient is unknown and set it from EMR if needed
    await setPatient(session);

    // if the visit has a patient, make sure the EMR is on that same
    // patient before we scrape questions or push answers into the chart.
    if (
      patientMatchWarning &&
      !skipPatientMatchCheck &&
      !patientWarningDismissed()
    ) {
      const mismatch = await checkPatientMatch(session);
      if (mismatch) {
        mismatchRef.current = mismatch;
        setPatientMismatch({
          ...mismatch,
          key,
          updateSectionStatusIndicator,
          source,
        });
        updateSectionStatusIndicator?.(key, 'pending');
        _setStatus('Check the patient open in your EMR');
        setAiThinking(false);
        return;
      }
    }

    // Grab the questions from the EMR and update the view to show these questions
    const loadTemplateResult = await loadTemplate(
      false,
      null,
      null,
      session,
      replyTypeAtComprehend,
    );
    // Switching browser tabs is fine; this stops only if the original EMR tab
    // itself changed to a different note/patient during the load.
    if (session.isDisposed() || !loadTemplateResult) {
      setAiThinking(false);
      return;
    }
    const {
      emrQuestions,
      configDetails,
      configId: generationConfigId,
    } = loadTemplateResult;
    // Pin the same pre-write note state Comprehend just scraped. Ordinary note
    // capture keeps both the latest and initial copies in sync until this point.
    session?.freezeInitialNote?.();
    // Use freshly loaded values directly from configDetails (do not use state as untill this function is done it will not be set)
    const template = configDetails.questions;
    const templateReplyForComprehend = configDetails.replyType;
    var localQuestions = emrQuestions;
    //Assign locationQuestions to a temp variable
    var tempLocationQuestions = localQuestions;
    // format that as a dict where ["Key"] is the key
    localQuestions = localQuestions.reduce(
      (a, b) => ({ ...a, [b['Key']]: b }),
      {},
    );
    // console.log("Scraped Questions"); console.log(localQuestions);

    // if questionsKeys is empty, do all questions show in the EMR... (Perhaps we should only do what is in the filtered?),
    // otherwise only do the question asked for
    let questionKeys;
    if (Array.isArray(key)) {
      questionKeys = key.filter((qKey) => qKey in localQuestions);
    } else if (typeof key === 'string') {
      questionKeys = [key];
    } else {
      questionKeys = Object.keys(localQuestions);
    }
    // snapshot sections being comprehended
    const questionKeySet = new Set(questionKeys);
    const emrQuestionsSnapshot = emrQuestions
      .filter((q) => questionKeySet.has(q.Key))
      .map((q) => ({
        Key: q.Key,
        Type: q.Type,
        ExistingNote: q.ExistingNote,
        Structure: q.Structure,
      }));
    if (emrQuestionsSnapshot.length > 0) {
      setEmrUndoStack((prevStack) => [
        ...prevStack,
        {
          session,
          questions: emrQuestionsSnapshot,
        },
      ]);
    }

    // What this run wrote into the EMR, by section key, so its rating can keep the
    // exact text it is about (ai_response is overwritten by
    // later runs and by compliance scoring).
    const generatedNote = {};
    const recordAnswer = (questionKey, answer) => {
      if (answer !== null && answer !== undefined) generatedNote[questionKey] = answer;
    };

    // Synchronously loop over the questions and ask the AI for the answer
    var questionsWereAnswered = questionKeys.join(', ');
    console.log('Comprehending Questions: ' + questionsWereAnswered);
    function chunkArray(array, size) {
      const result = [];
      for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
      }
      return result;
    }
    function groupQuestionsByType(localQuestions, template, questionKeys) {
      const shortAnswerQuestions = [];
      const multipleChoiceQuestions = [];
      const chooseAllQuestions = [];
      const otherQuestions = [];
      // Check the reply type, take overwrite by default - As this is the default in the selector!!!
      questionKeys.forEach((qkey) => {
        var replyType = templateReplyForComprehend[qkey] || 'overwrite';
        if (replyType !== 'no_reply') {
          const questionType = localQuestions[qkey]['Type'];
          if (questionType === 'Short Answer') {
            // Check if existingNote is true and the value for 'ExistingNote' exists
            const previousNote =
              existingNote && localQuestions[qkey]['ExistingNote']
                ? localQuestions[qkey]['ExistingNote']
                : null;
            const questionData = {
              questionKey: qkey,
              questionInstruction: template[qkey],
            };
            // Only add previousNote if it exists
            if (previousNote) {
              questionData.previousNote = previousNote;
            }
            shortAnswerQuestions.push(questionData);
          } else if (questionType === 'Multiple Choice') {
            multipleChoiceQuestions.push({
              questionKey: qkey,
              questionInstruction: template[qkey],
              choices: localQuestions[qkey]['Choices'],
            });
          } else if (questionType === 'Choose All') {
            chooseAllQuestions.push({
              questionKey: qkey,
              questionInstruction: template[qkey],
              choices: localQuestions[qkey]['Choices'],
            });
          } else {
            otherQuestions.push(qkey);
          }
        }
      });
      return {
        shortAnswerQuestions,
        multipleChoiceQuestions,
        chooseAllQuestions,
        otherQuestions,
      };
    }
    async function processQuestions(batch, answerType) {
      const responses = await apiClient.getConversationQuestions(
        conversationID,
        batch,
        answerType,
        isDowngraded,
      );

      if ('error' in responses) {
        if (
          responses.error.message &&
          responses.error.message.includes('rate limited')
        ) {
          setRateLimit(responses.error.refresh_limit);
          showRateLimitModal(); // Custom function to trigger the modal
          return; // Exit early if rate limited
        } else {
          _setStatus(console.log(responses), responses.error);
        }
        return;
      } else {
        for (const [question, answer] of Object.entries(responses['answers'])) {
          console.log(
            `${answerType} Question: ${question} --> Answer: ${answer}`,
          );

          if (answer === null || answer === undefined) {
            updateSectionStatusIndicator?.(question, 'none');
          }
          if (session.isDisposed()) {
            return;
          }
          await session.answerQuestionInEMR(
            localQuestions[question]['Question'],
            answer,
            templateReplyForComprehend[question] || 'overwrite',
            question,
          );
          if (session.isDisposed()) {
            return;
          }
          recordAnswer(question, answer);
          console.log('Answer Question In EMR:', answer); //temp delete later
        }
      }
    }

    var promises = [];
    if (questionBatching) {
      // Usage:
      const {
        shortAnswerQuestions,
        multipleChoiceQuestions,
        chooseAllQuestions,
        otherQuestions,
      } = groupQuestionsByType(localQuestions, template, questionKeys);

      // Batch and make API calls for each type of question
      const shortAnswerBatches = chunkArray(shortAnswerQuestions, 10);
      const multipleChoiceBatches = chunkArray(multipleChoiceQuestions, 10);
      const chooseAllBatches = chunkArray(chooseAllQuestions, 10);

      // Process other questions
      promises.push(
        ...otherQuestions.map(async (key) => {
          if (key === null || template[key] === undefined || !template[key]) {
            console.error(
              `During Batching, missing value for key: ${JSON.stringify(key)}. Template Value: ${template[key]}. TempQuestions before reduce: ${JSON.stringify(tempLocationQuestions)}. Local questions after reduce: ${JSON.stringify(localQuestions)}`,
            );
            return;
          }
          let filteredStructure = filterFlexStructureForNoReply(
            localQuestions[key],
            template[key],
          );

          var rsp = await apiClient.getConversationQuestion(
            conversationID,
            key,
            template[key],
            localQuestions[key]['Type'],
            localQuestions[key]['Choices'] || [],
            null,
            isDowngraded,
            filteredStructure,
            localQuestions[key].ParallelSections,
          );

          // Check if the AI understood the question and if so answer it in the EMR
          if ('error' in rsp) {
            updateSectionStatusIndicator?.(key, 'error');
            if (
              rsp.error.message &&
              rsp.error.message.includes('rate limited')
            ) {
              setRateLimit(rsp.error.refresh_limit);
              showRateLimitModal(); // Custom function to trigger the modal
              return; // Exit early if rate limited
            } else {
              _setStatus(console.log(rsp), rsp.error);
            }
            return;
          } else if (rsp['answer'] == null) {
            console.log('No answer from the AI');
            updateSectionStatusIndicator?.(key, 'none');
          } else {
            // Answer the question in the browser
            // TODO: Toss the key around, not the whole question...
            console.log('Other Answer: ' + rsp['answer']);
            if (session.isDisposed()) {
              return;
            }
            await session.answerQuestionInEMR(
              localQuestions[key]['Question'],
              rsp['answer'],
              parallelSectionReplyType(localQuestions[key], template[key], templateReplyForComprehend[key] || 'overwrite'),
              key,
            );
            if (session.isDisposed()) {
              return;
            }
            recordAnswer(key, rsp['answer']);
            if (hasNoneOrNullorTooBrief(rsp['answer'])) {
              updateSectionStatusIndicator?.(key, 'none');
            } else {
              updateSectionStatusIndicator?.(key, 'success');
            }
          }
        }),
      );

      // Process short answer questions
      promises.push(
        ...shortAnswerBatches.map((batch) =>
          processQuestions(batch, 'Short Answer'),
        ),
      );

      // Process multiple Chooce questions
      promises.push(
        ...multipleChoiceBatches.map((batch) =>
          processQuestions(batch, 'Multiple Choice'),
        ),
      );

      // Process choose all questions
      promises.push(
        ...chooseAllBatches.map((batch) =>
          processQuestions(batch, 'Choose All'),
        ),
      );
    } else {
      promises = questionKeys.map(async (key) => {
        updateSectionStatusIndicator?.(key, 'pending');
        // console.log("Comprehending Question"); console.log(localQuestions[key]); console.log("Question = " + template[key])
        // Ask the AI the question

        // Check the reply type, take overwrite by default - As this is the default in the selector!!!
        var replyType = templateReplyForComprehend[key] || 'overwrite';

        if (replyType === 'no_reply') {
          updateSectionStatusIndicator?.(key, 'success');
          console.log('Skipping Question');
          return;
        } else if (replyType === 'append') {
          // If we are appending, do not take into account the existing note?
          // I disagree so this will stay commented for now
          // localQuestions[key]['ExistingNote'] = null;
        }

        if (key === null || template[key] === undefined || !template[key]) {
          console.error(
            `Missing value for key: ${JSON.stringify(key)}. Template Value: ${template[key]}. TempQuestions before reduce: ${JSON.stringify(tempLocationQuestions)}. Local questions after reduce: ${JSON.stringify(localQuestions)}`,
          );
          updateSectionStatusIndicator?.(key, 'error');
          return;
        }
        try {
          let filteredStructure = filterFlexStructureForNoReply(
            localQuestions[key],
            template[key],
          );

          var rsp = await apiClient.getConversationQuestion(
            conversationID,
            key,
            template[key],
            localQuestions[key]['Type'],
            localQuestions[key]['Choices'],
            existingNote ? localQuestions[key]['ExistingNote'] : null,
            isDowngraded,
            filteredStructure,
            localQuestions[key].ParallelSections,
          );
        } catch (err) {
          if (apiClient.isTimeoutError(err)) {
            console.log(`Timeout for ${key}, retrying...`);
            return { timeout: key };
          } else {
            throw err;
          }
        }
        // console.log("Got AI's response to the question")
        // console.log(rsp)

        // Check if the AI understood the question and if so answer it in the EMR
        if ('error' in rsp) {
          updateSectionStatusIndicator?.(key, 'error');
          if (rsp.error.message && rsp.error.message.includes('rate limited')) {
            setRateLimit(rsp.error.refresh_limit);
            showRateLimitModal(); // Custom function to trigger the modal
            return; // Exit early if rate limited
          } else {
            console.log('Error comprehending question');
            _setStatus(console.log(rsp), rsp.error);
          }
          return;
        } else if (rsp['answer'] == null) {
          console.log('No answer from the AI');
          updateSectionStatusIndicator?.(key, 'none');
        } else {
          // Answer the question in the browser
          // TODO: Toss the key around, not the whole question...
          if (session.isDisposed()) {
            return;
          }
          await session.answerQuestionInEMR(
            localQuestions[key]['Question'],
            rsp['answer'],
            parallelSectionReplyType(localQuestions[key], template[key], replyType),
            key,
          );
          if (session.isDisposed()) {
            return;
          }
          recordAnswer(key, rsp['answer']);
          if (hasNoneOrNullorTooBrief(rsp['answer'])) {
            updateSectionStatusIndicator?.(key, 'none');
          } else {
            updateSectionStatusIndicator?.(key, 'success');
          }
        }
      });
    }

    // wait for all of them to finish
    try {
      const results = await Promise.all(promises);
      // If any were timeouts, do your best to still get them...
      await tryAnyTimeouts(
        results,
        localQuestions,
        updateSectionStatusIndicator,
        session,
        templateReplyForComprehend,
        template,
        recordAnswer,
      );
      if (session.isDisposed()) {
        setAiThinking(false);
        return;
      }
      // Finish the post-write scrape before this session can be run again.
      await session.linkNoteToConversation?.(
        conversationID,
        parseInt(generationConfigId, 10),
      );
      // Step 1: Comprehension Complete
      _setStatus('✅ Comprehension Complete!');
      // Onboarding: a note written outside the Playground is the real thing.
      try {
        const h = window.location.hostname;
        const inPlayground = /^playground\./.test(h);
        if (!inPlayground && apiClient.markOnboarding) apiClient.markOnboarding('real');
      } catch (e) { /* never block the note */ }
      setCodingAutoRun({ conversationID, session });
      setAiThinking(false);
      if (apiClient.showComplianceScore) {
        setComplianceScoreLoading(true);
        _setComplianceScore(0); // Clear stale score so old grade doesn't persist during fetch

        (async () => {
          try {
            const existingValuesMapping = await getExistingValuesFromEMR(session);
            if (session.isDisposed()) {
              return;
            }

            // Which compliance rule set grades this note is decided by the EMR's
            // note metadata, not by the clinician. Read it from the same session
            // so it can't drift to a different visit mid-score.
            const emrMetadata = await getEmrMetadata(session);
            if (session.isDisposed()) {
              return;
            }

            let response;
            try {
              response = await Promise.race([
                apiClient.scoreConversation(
                  conversationID,
                  existingValuesMapping,
                  emrMetadata,
                ),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('SCORE_TIMEOUT')), 28000),
                ),
              ]);
            } catch (raceErr) {
              if (raceErr.message === 'SCORE_TIMEOUT') {
                // Score endpoint timed out — poll conversation details for the saved score
                for (let i = 0; i < 24; i++) {
                  await new Promise((r) => setTimeout(r, 5000));
                  const data =
                    await apiClient.getConversationDetails(conversationID);
                  if (data?.score) {
                    _setComplianceScore(data.score);
                    const feedbackToUse =
                      data.requirement_evaluations || data.feedback || [];
                    _setComplianceFeedback(feedbackToUse);
                    _setStatus('🚀 Comprehension and Compliance Complete!');
                    return;
                  }
                }
                _setStatus(
                  '❗Compliance unavailable. See "How to Improve?" for details',
                );
                return;
              }
              throw raceErr;
            }

            if (response?.error) {
              throw new Error(
                response.error.message ||
                  response.error ||
                  'Compliance scoring failed',
              );
            }
            if (!response?.answer) {
              throw new Error('Compliance scoring failed: no response payload');
            }
            // Destructure response to get score and feedback
            const { score, feedback, requirement_evaluations } =
              response.answer;
            // Update compliance score and feedback
            _setComplianceScore(score);
            // Prefer requirement_evaluations if available, otherwise fall back to feedback
            const feedbackToUse = requirement_evaluations || feedback || [];
            _setComplianceFeedback(feedbackToUse);
            // Update status and UI
            _setStatus('🚀 Comprehension and Compliance Complete!');
          } catch (scoringError) {
            console.error('Error in scoring conversation:', scoringError);
            _setStatus(
              '❗Compliance unavailable. See "How to Improve?" for details',
            );
            const errorMessage =
              scoringError?.response?.data?.error?.message ||
              scoringError?.response?.data?.error ||
              scoringError?.message ||
              scoringError?.toString?.() ||
              'Compliance scoring failed';
            showToast(errorMessage);
          } finally {
            setComplianceScoreLoading(false);
          }
        })();
      }
      const runSections = [].concat(key ?? []);
      startFeedbackRun({
        pageInfo,
        conversationId: conversationID,
        source,
        generatedSections: runSections.length ? runSections : null, // null = whole note
        generatedNote: { ...generatedNote },
      });
      return true;
    } catch (comprehensionError) {
      // Handle errors if any of the promises fail
      console.log(`Error: ${comprehensionError}`);
      setAiThinking(false);
      _setStatus('❌ Comprehension failed. Please try again.');
      // `key` is undefined when the whole note is comprehended, so marking only
      // `key` left every section stuck on its spinner. Fail them all visibly.
      (questionKeys || []).forEach((failedKey) =>
        updateSectionStatusIndicator?.(failedKey, 'error'),
      );
    }
  }

  async function tryAnyTimeouts(
    result,
    localQuestions,
    updateSectionStatusIndicator,
    session = null,
    templateReplyForComprehend = templateReply,
    templateForComprehend = template,
    onAnswered = null,
  ) {
    /**
     * Description: This function will take a list of promises and if any of them are timeouts, it will retry them
     *
     * Args: result: a list of values, where some might be dictionaries with a timeout param that has a value of the key we want
     * localQuestions: a local var we need to answer the question
     *
     * Return: None
     */
    // make a list of the promises that are timeouts
    var timeoutKeys = [];
    for (var i = 0; i < result.length; i++) {
      if (result[i] && result[i].timeout) {
        timeoutKeys.push(result[i].timeout);
      }
    }
    // Nothing timed out - don't tell the user to hang in there and don't spend
    // a round trip on details we aren't going to read.
    if (timeoutKeys.length === 0) {
      return;
    }

    setBannerText('Hang in there...');
    const originalDetails =
      await apiClient.getConversationDetails(conversationID);
    const originalQs = originalDetails.questions;

    const MaxWait = 120; // Max wait time
    const interval = 5; // Interval in second
    const startTime = Math.floor(Date.now() / 1000);
    /**
     * Poll the conversationDetails every 5 seconds for up to MaxWait seconds waiting to
     *      see if the answers populate. and when they do, answer it in the EMR
     */
    while (
      timeoutKeys.length > 0 &&
      Math.floor(Date.now() / 1000) - startTime < MaxWait
    ) {
      // Stop polling if the session went stale (tab closed or patient
      // changed) — the user has already been toasted and writes will no-op.
      if (session?.isDisposed?.()) {
        return;
      }
      if (Math.floor(Date.now() / 1000) - startTime > 60) {
        setBannerText('Still Comprehending...');
      }

      // sleep for 5 seconds
      await new Promise((resolve) => setTimeout(resolve, interval * 1000));

      let updatedDetails =
        await apiClient.getConversationDetails(conversationID);
      let updatedQs = updatedDetails.questions;
      // The backend records per-section failures here. /question runs past the
      // gateway's ~30s ceiling, so a section that errored server-side used to
      // look identical to one still being written - we polled it to the end of
      // MaxWait and then left the spinner going forever.
      const backendErrors = updatedDetails.question_errors || {};
      const stillWaiting = [];
      for (const qKey of timeoutKeys) {
        if (backendErrors[qKey]) {
          console.error(
            `Comprehend failed server-side for ${qKey}:`,
            backendErrors[qKey],
          );
          updateSectionStatusIndicator?.(qKey, 'error');
          continue;
        }
        if (originalQs[qKey] === updatedQs[qKey]) {
          console.log(`No change for ${qKey}, not answering yet`);
          stillWaiting.push(qKey);
          continue;
        }
        if (session) {
          await session.answerQuestionInEMR(
            localQuestions[qKey]['Question'],
            updatedQs[qKey],
            parallelSectionReplyType(localQuestions[qKey], templateForComprehend[qKey], templateReplyForComprehend[qKey] || 'overwrite'),
            qKey,
          );
          if (session.isDisposed()) {
            return;
          }
        } else {
          answerQuestionInEMR(
            localQuestions[qKey]['Question'],
            updatedQs[qKey],
            parallelSectionReplyType(localQuestions[qKey], templateForComprehend[qKey], templateReplyForComprehend[qKey] || 'overwrite'),
          );
        }
        onAnswered?.(qKey, updatedQs[qKey]);
        if (hasNoneOrNullorTooBrief(updatedQs[qKey])) {
          updateSectionStatusIndicator?.(qKey, 'none');
        } else {
          updateSectionStatusIndicator?.(qKey, 'success');
        }
      }
      timeoutKeys = stillWaiting;
    }

    // Whatever is still unanswered when the window closes is not coming. Leaving
    // these on 'pending' is what left the section spinning indefinitely.
    if (timeoutKeys.length > 0) {
      console.error(
        `Gave up waiting on: ${timeoutKeys.join(', ')} after ${MaxWait}s`,
      );
      for (const qKey of timeoutKeys) {
        updateSectionStatusIndicator?.(qKey, 'timeout');
      }
      showToast(
        `Comprehend timed out on: ${timeoutKeys.join(', ')}. Use the section's refresh button to try again.`,
      );
    }
    return;
  }

  function setTemplateReply(fieldName, newValue) {
    const replyTypeConfigID = templateReplyConfigIDRef.current;
    replyTypeRevisionsRef.current[replyTypeConfigID] = (replyTypeRevisionsRef.current[replyTypeConfigID] || 0) + 1;
    apiClient
      .editConfig(replyTypeConfigID, 'replyType', fieldName, newValue)
      .then((data) => {
        if ('error' in data) {
          console.log(`Error editing template - configID = ${replyTypeConfigID}`);
          console.log(data);
          return;
        } else {
          console.log(`Updated template reply type - configID = ${replyTypeConfigID}`);
          console.log(data);
        }
      })
      .catch((error) => {
        console.log(`Unexpected Error from template edit - ${error}`);
      });

    // Update the local copy of replyType to reflect the change of the dictionary
    console.log(`Updating Reply Type - ${fieldName} = ${newValue}`);
    _setTemplateReply((currentReplyTypes) => ({
      ...currentReplyTypes,
      [fieldName]: newValue,
    }));
  }

  const disableEditing = viewMode === 'View Only' || viewMode === 'Duplicate';
  const showInstructionAssistant = !disableEditing && viewMode !== 'AutoCopy';
  // Push-to-talk into Clinical Facts. Flagged off by default; the field stays
  // typeable either way, so a disabled flag only hides the button.
  const showDictation = !!apiClient.features?.dictation;
  // Comprehend only needs its own row once Dictation is taking half the first
  // one; on its own, Live Visit leaves room to sit beside it.
  const showDictationButton = showDictation && showWriteNotes;
  const showLearnButton = learnFeatureFlag && !disableEditing;
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Handler to update online/offline internet status
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // The item the compliance rail asked this visit to open on — any of its
  // buttons: 'note', 'chat', 'compliance', 'coding', 'app:<id>', 'apps'
  // (index.js puts the request in location.state). Null when the visit was
  // opened any other way.
  const landingTabRef = useRef(location.state?.landingTab || null);
  // The deleted visit already swapped for a new one, so a repeat load can't make two.
  const replacedVisitRef = useRef(null);

  // While the note type is still being read from the EMR, show the
  // Comprehend ⇄ EMR animation in its place. Once the note type arrives, hold
  // it a further second so the sections finish rendering out of sight; give
  // up after a while so a visit with no note open falls back to the usual
  // prompt. Once revealed, a visit never goes back to it (picking another
  // template doesn't blank the note).
  const [emrRevealed, setEmrRevealed] = useState(false);
  useEffect(() => {
    setEmrRevealed(false);
  }, [conversationID]);
  useEffect(() => {
    if (emrRevealed) return undefined;
    const t = setTimeout(() => setEmrRevealed(true), config === defaultConfig ? 12000 : 1000);
    return () => clearTimeout(t);
  }, [config, conversationID, emrRevealed]);
  const emrConnecting = !emrRevealed;

  /* Conditional showing of the comprehend button */
  const [disableComprehend, setDisableComprehend] = useState(false);
  useEffect(() => {
    if (status === 'Start Recording!') {
      setDisableComprehend(true);
      setTabsVisible(false); // Hide tabs when recording hasn't started
      console.log('****');
    } else if (status === 'Uploading Audio...') {
      setBannerText('Uploading Audio...');
      setAiThinking(true);
      setDisableComprehend(true);
    } else {
      // Only hide banner if we're not in another AI thinking state and the banner is for uploading
      if (
        bannerText === 'Uploading Audio...' &&
        status !== 'Comprehending...' &&
        !status.includes('Compliance')
      ) {
        setAiThinking(false);
      }
      setDisableComprehend(false);
      setTabsVisible(true); // Show tabs when not in "Start Recording!" status
      // No tab switch here: a status change (a sync or Comprehend finishing)
      // leaves the clinician on whatever tab they're on. The visit opens on
      // their first tab (or the rail's) once, below; after that, only a click
      // moves them.
    }
  }, [status, bannerText]);

  // Land on the rail's requested tab once the details have loaded.
  // With nothing requested, the visit opens on the clinician's first tab —
  // Note unless they moved something ahead of it. A connected app's tab can
  // only open once the app list has arrived, so this also waits for the item.
  const landingTabAppliedRef = useRef(false);
  const landingTarget =
    landingTabRef.current || (workspace.loaded ? workspace.pinned[0]?.id || 'note' : null);
  const landingAvailable =
    landingTarget === APPS_ID || Boolean(workspace.byId[landingTarget]);
  // Each visit lands once: a new visit in the same panel (New, or another
  // from the list) opens on the first tab again. The rail's request was for
  // the visit it opened, so later visits go to the first tab.
  useEffect(() => {
    if (landingTabAppliedRef.current) landingTabRef.current = null;
    landingTabAppliedRef.current = false;
  }, [conversationID]);
  useEffect(() => {
    if (!landingTarget || landingTabAppliedRef.current) return;
    if (status === defaultStatus || !landingAvailable) return;
    landingTabAppliedRef.current = true;
    workspace.open(landingTarget);
  }, [status, landingAvailable, landingTarget, conversationID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showEditorFields === 'visit' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showEditorFields]);

  const handleLearnTemplate = async () => {
    setBannerText('Learning (you can leave this page)');
    setAiThinking(true);
    logGAEvent('learn_clicked', apiClient, {
      configID: configID,
      conversationID: conversationID,
    });
    try {
      const { questions: emrQuestions, dropdownMisses } =
        await getEMRQuestionsWithCache();

      sendDropDownMissesToSlack(dropdownMisses, configID, conversationID);

      // Respect skip logic: do not Learn from sections marked as "Skip" (replyType === 'no_reply').
      const questionsForLearn = (emrQuestions || []).filter((q) => {
        const rt =
          (templateReply && q && q.Key && templateReply[q.Key]) || 'overwrite';
        return rt !== 'no_reply';
      });

      // Keep the finished note as a Re-Learn My Style example, even when Learn
      // finds nothing to change. Not awaited: Learn never waits on it.
      const learnExample = {};
      questionsForLearn.forEach((q) => {
        if (q && q.Key) learnExample[q.Key] = getExistingDataForLearn(q, false);
      });
      saveLearnExample(apiClient, conversationID, configID, learnExample);

      // Agentic Learn hands the whole note to the agent. Whatever it does not
      // accept falls through to Learn / Learn++ below, unchanged.
      if (
        isAgenticLearnUser(apiClient) &&
        (await handleLearnTemplateAgentic(questionsForLearn))
      ) {
        return;
      }

      // Learn++ runs an entirely separate path (single whole-note call).
      // It returns early so the legacy flow below is byte-for-byte what it
      // was on main — existing users are unaffected by this feature.
      if (apiClient.features?.learn_plus) {
        await handleLearnTemplatePlus(questionsForLearn);
        return;
      }

      const results = await learnTemplateSections(
        apiClient,
        conversationID,
        configID,
        questionsForLearn.map((q) => ({
          key: q.Key,
          usersEdits: getExistingDataForLearn(q),
        })),
      );
      const learnedChanges = results.some(
        (result) =>
          result &&
          (result.newInstruction ||
            result.newExamples ||
            result.newSubsections),
      );
      // Process results to update samples and instructions
      const newTemplate = { ...templateFiltered };
      const newSamples = { ...samples };
      const newSubsections = { ...subsections };
      results.forEach((result) => {
        if (result && result.justification) {
          console.log(
            `--------------${result.key}-----------------\n` +
              `Justification: ${result.justification}\n` +
              `-----------------------------------------\n` +
              `Learn Template Result:`,
            result,
            `-----------------------------------------\n`,
          );
        }

        // Update template instruction
        if (result && result.newInstruction) {
          newTemplate[result.key] = result.newInstruction;
        }

        // If there are new examples, update samples
        if (result && result.newExamples) {
          newSamples[result.key] = result.newExamples;
        }
        if (result && result.newSubsections) {
          newSubsections[result.key] = result.newSubsections;
        }
      });
      _setTemplateFiltered(newTemplate);
      _setSamples(newSamples);
      _setSubsections(newSubsections);
      // // Send updates to server - it should already be there unless we have a race condition...
      // setTemplate(newTemplate, configID, template, { [result.key]: result.newExamples });
      //   } else {
      // // Just update the instruction if no examples
      // setTemplate(newTemplate, configID);

      // A failed section names itself in the banner, so leave it up longer.
      setBannerText(learnResultBanner(results, learnedChanges));
      setTimeout(
        () => setAiThinking(false),
        results.some((result) => result.failed) ? 6000 : 2000,
      );
    } catch (error) {
      console.error('Learn template error:', error);
      setAiThinking(false);
    }
  };

  // Learn++ (feature flag `learn_plus`). Deliberately self-contained: one
  // whole-note call instead of one per section, with its own result handling
  // so the legacy learn flow above stays untouched for existing users.
  const handleLearnTemplatePlus = async (questionsForLearn) => {
    const usersEdits = {};
    questionsForLearn.forEach((q) => {
      const edits = getExistingDataForLearn(q);
      if (q.Key && edits) {
        usersEdits[q.Key] = edits;
      }
    });

    const learnResult = await apiClient.learnNote(
      conversationID,
      configID,
      usersEdits,
    );
    const sectionResults = (learnResult && learnResult.sections) || {};
    if (learnResult && learnResult.learnLoop) {
      console.log('Learn accuracy loop:', learnResult.learnLoop);
    }

    const newTemplate = { ...templateFiltered };
    const newSamples = { ...samples };
    const newSubsections = { ...subsections };
    Object.entries(sectionResults).forEach(([key, result]) => {
      if (result && result.justification) {
        console.log(
          `--------------${key}-----------------\n` +
            `Justification: ${result.justification}\n` +
            `-----------------------------------------\n` +
            `Learn Template Result:`,
          result,
          `-----------------------------------------\n`,
        );
      }
      if (result && result.newInstruction) {
        newTemplate[key] = result.newInstruction;
      }
      if (result && result.newExamples) {
        newSamples[key] = result.newExamples;
      }
      if (result && result.newSubsections) {
        newSubsections[key] = result.newSubsections;
      }
    });
    _setTemplateFiltered(newTemplate);
    _setSamples(newSamples);
    _setSubsections(newSubsections);

    const learnedChanges = Object.values(sectionResults).some(
      (result) =>
        result &&
        (result.newInstruction || result.newExamples || result.newSubsections),
    );
    setBannerText(learnedChanges ? 'Edits Learned ✅' : 'No edits detected');
    setTimeout(() => setAiThinking(false), 2000);
  };

  // The template that wrote the note, for a thumbs down handed to Agentic Learn.
  const agenticLearnConfigIDRef = useRef(configID);
  agenticLearnConfigIDRef.current = configID;

  // Agentic Learn (features.signed_note_learner === 'agentic_learn'). Returns
  // true when the agent took the note (or there were no edits), false to run
  // the existing Learn path instead. Looks like Learn to the clinician: the
  // agent works in the background and says nothing when it is done.
  const handleLearnTemplateAgentic = async (questionsForLearn) => {
    const usersEdits = {};
    questionsForLearn.forEach((q) => {
      const edits = getExistingDataForLearn(q);
      if (q.Key && edits) {
        usersEdits[q.Key] = edits;
      }
    });

    let response = null;
    try {
      response = await apiClient.triggerAgenticLearn(
        configID,
        conversationID,
        'learn',
        { usersEdits },
      );
    } catch (error) {
      return false;
    }
    if (response?.reason === 'no_edits') {
      setBannerText('No edits detected');
      setTimeout(() => setAiThinking(false), 2000);
      return true;
    }
    if (!response?.accepted) {
      return false;
    }

    setBannerText('Edits Learned ✅');
    setTimeout(() => setAiThinking(false), 2000);
    return true;
  };

  function getExistingData(q) {
    if (!q.ExistingNote && q.Structure) {
      q.ExistingNote = q.Structure;
    }
    return q.ExistingNote;
  }

  // Wrapper function to get existing values from EMR scraping
  const getExistingValuesFromEMR = async (session = null) => {
    if (session?.isDisposed?.()) {
      return {};
    }

    // Check if EMR page is open and note type is available
    const pageInfo = session
      ? await session.getPageInfo()
      : await getPageInfo();
    if (!pageInfo || !pageInfo['URL'] || !pageInfo['Note Type']) {
      showToast(
        '⚠️ Open up the completed patient note in your EMR to get Compliance score',
      );
      return {};
    }

    // Step 1: Scrape EMR to get questions with existing values
    const { questions: emrQuestions } = await getEMRQuestionsWithCache(
      null,
      session
    );

    // Step 2: Create mapping of {q.Key: getExistingData(q)} with only "Existing Value" fields
    const existingValuesMapping = {};
    emrQuestions.forEach((q) => {
      if (q.Key) {
        const existingValue = getExistingData(q);
        if (existingValue) {
          // Extract only "Existing Value" fields and remove "Description" fields
          existingValuesMapping[q.Key] =
            extractExistingValuesOnly(existingValue);
        }
      }
    });

    return existingValuesMapping;
  };

  // The EMR's own note metadata, when it publishes any. CORA Sapphire embeds a
  // noteMetadata JSON blob whose isMC / isWC / isPeds flags tell the backend
  // which compliance rule set to grade this note against, so the clinician never
  // has to know which payer the visit is for. Null on every other EMR.
  const getEmrMetadata = async (session = null) => {
    if (session?.isDisposed?.()) {
      return null;
    }
    try {
      const metadata = session?.getPageMetadata
        ? await session.getPageMetadata()
        : await getPageMetadata();
      return metadata && typeof metadata === 'object' ? metadata : null;
    } catch (err) {
      console.log('Could not read EMR note metadata:', err);
      return null;
    }
  };

  const handleGetComplianceScore = async () => {
    await handleGetComplianceScoreUtil({
      conversationID,
      complianceScoreLoading,
      setComplianceScoreLoading,
      apiClient,
      getExistingValuesMapping: getExistingValuesFromEMR,
      getEmrMetadata,
      _setComplianceScore,
      _setComplianceFeedback,
      _setStatus,
    });
  };

  // Always holds the latest handleGetComplianceScore so the pen-click listener
  // registered once on mount doesn't capture a stale closure.
  const handleGetComplianceScoreRef = useRef(handleGetComplianceScore);
  useEffect(() => {
    handleGetComplianceScoreRef.current = handleGetComplianceScore;
  });

  // Tracks whether the score refresh was triggered by the pen click (vs. initial load).
  // When true, the result modal opens automatically after the API returns.
  const penRecheckPendingRef = useRef(false);

  useEffect(() => {
    const cleanup = setupCompliancePenClickCallback(() => {
      penRecheckPendingRef.current = true;
      handleGetComplianceScoreRef.current();
    });
    return () => { if (typeof cleanup === 'function') cleanup(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Omits "Description" and "Choices" fields and collapses empty sections for Learn, since these are not relevant for the AI's learning and can cause confusion if left in.
  function omitField(data, field, collapseEmpty = false) {
    if (Array.isArray(data)) {
      return data
        .map((v) => omitField(v, field, collapseEmpty))
        .filter((v) => v !== undefined);
    }
    if (data && typeof data === 'object') {
      const result = {};
      for (const [k, v] of Object.entries(data)) {
        if (k === field) continue;
        const processed = omitField(v, field, collapseEmpty);
        if (processed !== undefined) {
          result[k] = processed;
        }
      }
      return collapseEmpty && Object.keys(result).length === 0
        ? undefined
        : result;
    }
    return data;
  }

  function omitLearnFields(data) {
    return omitField(omitField(data, 'Description'), 'Choices', true);
  }

  // For Learn we also need to respect subsection-level skip logic for Flex structures.
  function getExistingDataForLearn(q, omitMetadata = true) {
    if (!q) return null;

    // Flex questions can contain subsection replyTypes inside the template JSON; filter out 'no_reply' subsections.
    if (q.Type === 'Flex') {
      // remove prune_early from the structure
      if (q.Structure) {
        delete q.Structure.prune_early;
      }
      const templateValue =
        (templateFiltered && q.Key && templateFiltered[q.Key]) || null;
      const value = filterFlexStructureForNoReply(q, templateValue);
      return omitMetadata ? omitLearnFields(value) : value;
    }

    const value = getExistingData(q);
    return omitMetadata ? omitLearnFields(value) : value;
  }

  const fromMultiRecorder = location.state?.from === 'multi-recorder';
  const breadcrumbItems = [
    fromMultiRecorder
      ? { name: 'Multi Recorder', path: '/User/conversations/multi-recorder' }
      : { name: 'Visits', path: '/User/conversations' },
    {
      name: (
        <span
          onClick={() => setShowEditorFields('patient')}
          style={{ cursor: 'pointer' }}
        >
          <i className="fa fa-user"></i>
          {` ${truncateText(group || 'Unknown')}`}
          <i className="fa fa-pen" style={{ marginLeft: '8px' }}></i>
        </span>
      ),
      path: '',
    },
    {
      name: (
        <span
          onClick={() => setShowEditorFields('visit')}
          style={{ cursor: 'pointer' }}
        >
          <i className="fa fa-file-text"></i>
          {` ${truncateText(name || 'Untitled')}`}
          <i className="fa fa-pen" style={{ marginLeft: '8px' }}></i>
        </span>
      ),
      path: '',
    },
  ];

  // Shows the feedback bar for a run comprehendTemplate just completed.
  const startFeedbackRun = ({ pageInfo, conversationId, source, generatedSections, generatedNote }) => {
    // Only the host is kept: a full EMR URL can carry patient identifiers.
    let emrHost = null;
    try {
      // Some injectors report a bare domain ("charmtracker.com") with no scheme.
      const pageUrl = String(pageInfo.URL || '');
      emrHost = new URL(/^[a-z]+:\/\//i.test(pageUrl) ? pageUrl : `https://${pageUrl}`).hostname;
    } catch (e) {
      // No usable page URL; the rating is still saved without an EMR.
    }
    setFeedbackRun({
      id: Date.now(),
      conversationId,
      emrHost,
      noteType: pageInfo['Note Type'] || null,
      source,
      generatedSections,
      generatedNote,
      // Not sent with the rating; Agentic Learn tunes the template that wrote the note.
      configId: agenticLearnConfigIDRef.current,
    });
  };

  const dispatchComprehendStatus = (key, status) => {
    // This updates each section's refresh button via the callback
    document.dispatchEvent(
      new CustomEvent('updateComprehendStatus', {
        detail: { key, status },
      }),
    );
  };

  const handleComprehendClick = async (fromModal = false) => {
    const pageInfo = await getPageInfo();
    // The full-note injector opens Yes/No-gated fields itself and fills every
    // tab, so the "pick your radios first" prompt no longer applies.
    if (
      pageInfo.URL.includes('app.webpt.com') &&
      apiClient.features?.new_webpt_integration !== true &&
      localStorage.getItem('comprehend-highlight-modal') !== 'true' &&
      !fromModal
    ) {
      await highlightFields();
      setShowHighlightModal(true);
      return;
    }
    await comprehendTemplate(undefined, dispatchComprehendStatus);
  };

  // Headless (from the visits list): Comprehend once the visit has loaded.
  const autoComprehendRef = useRef(headless);
  useEffect(() => {
    if (!autoComprehendRef.current) return;
    if (
      !transcript ||
      disableComprehend ||
      [defaultStatus, 'Start Recording!', 'Uploading Audio...'].includes(status)
    )
      return;
    autoComprehendRef.current = false;
    comprehendTemplate(undefined, null, skipPatientCheck)
      .catch((error) => console.error('Comprehend from the list failed', error))
      .then((completed) =>
        onDone?.({ completed: completed === true, mismatch: mismatchRef.current }),
      );
  }, [status, disableComprehend, transcript]); // eslint-disable-line react-hooks/exhaustive-deps
  // The list allows one Comprehend at a time; if this visit never becomes ready, hand back.
  useEffect(() => {
    if (!headless) return undefined;
    const timer = setTimeout(() => {
      if (!autoComprehendRef.current) return;
      autoComprehendRef.current = false;
      onDone?.({ completed: false, mismatch: null });
    }, HEADLESS_START_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleComprehendFeedback = (feedback) => {
    apiClient
      .saveComprehendFeedback(feedbackRun.conversationId, {
        runId: feedbackRun.id,
        emrHost: feedbackRun.emrHost,
        noteType: feedbackRun.noteType,
        source: feedbackRun.source,
        generatedSections: feedbackRun.generatedSections,
        // Re-sent with every answer; the server keeps the first copy it stores.
        generatedNote: feedbackRun.generatedNote,
        ...feedback,
      })
      .catch((error) => console.error('Failed to save Comprehend feedback', error));

    // Agentic Learn also gets a thumbs down that came with details ("Send
    // feedback"), not the bare click. Fire and forget.
    const hasDetails =
      feedback.rating === 'down' &&
      (feedback.reasons?.length > 0 ||
        feedback.sections?.length > 0 ||
        !!feedback.comment);
    const writtenFeedback = (feedback.comment || '').trim();
    if (writtenFeedback && showInstructionAssistant) {
      setInstructionAssistantFeedback(writtenFeedback);
      setInstructionAssistantOpen(true);
    }
    if (
      hasDetails &&
      isAgenticLearnUser(apiClient) &&
      feedbackRun.configId &&
      feedbackRun.configId !== -1
    ) {
      apiClient
        .triggerAgenticLearn(feedbackRun.configId, feedbackRun.conversationId, 'thumbs_down', {
          runId: feedbackRun.id,
          reasons: feedback.reasons || [],
          sections: feedback.sections || [],
          comment: feedback.comment || '',
        })
        .catch(() => {});
    }
  };

  // Update actionsRef with current handlers and state for external access (tools, etc)
  actionsRef.current = {
    comprehendTemplate,
    setTranscript,
    setConfig,
    transcript,
  };

  const getInstructionAssistantSectionMetadata = async () => {
    const hasConfig = configID && configID !== -1;
    lastScrapedQuestionsRef.current = null;
    const loadResult = await loadTemplate(!hasConfig);
    const currentQuestions = lastScrapedQuestionsRef.current || [];
    if (!loadResult || currentQuestions.length === 0) {
      throw new Error('Could not scan the current note.');
    }
    const effectiveConfigId = loadResult.configId || configID;
    if (!effectiveConfigId || effectiveConfigId === -1) {
      throw new Error('Could not scan the current note.');
    }
    return {
      sectionMetadata: buildSectionMetadata(
        currentQuestions,
        loadResult.configDetails?.subsections || {},
      ),
      configId: effectiveConfigId,
    };
  };

  const loadVisitTemplate = async () => {
    const hasConfig = configID && configID !== -1;
    const loadResult = await loadTemplate(!hasConfig);
    if (!loadResult?.configId || loadResult.configId === -1) {
      throw new Error('Could not load a template for the note open in your EMR.');
    }
    return loadResult;
  };

  // Show saved instruction changes in the panel (Instruction Assistant, chat)
  const applyLocalInstructions = (revisedInstructions) => {
    _setTemplate((currentTemplate) => ({
      ...currentTemplate,
      ...revisedInstructions,
    }));
    _setTemplateFiltered((currentVisibleTemplate) => {
      const updatedVisibleTemplate = { ...currentVisibleTemplate };
      for (const sectionKey of Object.keys(revisedInstructions)) {
        if (sectionKey in updatedVisibleTemplate) {
          updatedVisibleTemplate[sectionKey] =
            revisedInstructions[sectionKey];
        }
      }
      return updatedVisibleTemplate;
    });
  };

  // ---- Instruction Assistant: apply + undo lifecycle ----
  // The generic state machine lives in useInstructionAssistantUpdate;
  // the host supplies instruction, example, and note adapters for local state and EMR work.
  const instructionAssistantUpdate = useInstructionAssistantUpdate({
    apiClient,
    configId: configID,
    conversationId:
      conversationID && conversationID !== -1 ? conversationID : null,
    instructions: {
      getCurrent: () => template,
      applyLocal: applyLocalInstructions,
      getSectionMetadata: () =>
        buildSectionMetadata(
          lastScrapedQuestionsRef.current,
          subsections,
        ),
    },
    examples: {
      getCurrent: () => samples,
      applyLocal: (revisedExamples) =>
        _setSamples((currentSamples) => ({
          ...currentSamples,
          ...revisedExamples,
        })),
    },
    note: {
      snapshot: async (changedKeys) => {
        const currentQuestions = lastScrapedQuestionsRef.current || [];
        if (currentQuestions.length === 0) {
          throw new Error('Scan the current note before applying changes.');
        }
        return (currentQuestions || [])
          .filter((q) => changedKeys.includes(q.Key))
          .map((q) => ({
            Key: q.Key,
            Question: q.Question,
            Answer:
              q.Type === 'Flex' && q.Structure
                ? flattenFlexStructure(q.Structure, q.ParallelSections)
                : q.ExistingNote ?? '',
          }));
      },
      reComprehend: async (changedKeys) => {
        await comprehendTemplate(changedKeys, dispatchComprehendStatus, {
          source: FEEDBACK_SOURCES.instructionAssistant,
        });
      },
      revert: (answers) => answerQuestionsInEMR(answers),
    },
    onError: (message) => showToast(message),
  });
  const resetInstructionAssistantUpdate = instructionAssistantUpdate.reset;

  // ---- Agentic chat (COM-536): this view's note functions as chat tools ----
  // Refreshed every render so the tools always see current state; the tools
  // themselves live in components/ChatAgent/visitNoteTools.
  const chatAgentHostRef = useRef(null);
  chatAgentHostRef.current = {
    apiClient,
    conversationId: conversationID,
    configId: configID,
    transcript,
    visitNotes,
    templateReply,
    createEMRSession,
    scanNote: async (session) => {
      await getEMRQuestionsWithCache(null, session);
      return lastScrapedQuestionsRef.current || [];
    },
    checkPatientMatch,
    // The visit patient's names, so chat keeps them out of template examples
    getPatientNames: () => {
      const visitPatient = apiClient.patients?.find((p) => String(p.uid) === String(groupId));
      return [visitPatient?.firstName, visitPatient?.lastName].filter(Boolean);
    },
    setPatient,
    comprehendTemplate,
    dispatchComprehendStatus,
    pushEmrUndo: (session, questions) =>
      setEmrUndoStack((prevStack) => [...prevStack, { session, questions }]),
    getStatus: () => status,
    appendVisitNotes,
    setVisitNotes,
    setTemplateReply,
    applyLocalInstructions,
    applyLocalExamples: (revisedExamples) =>
      _setSamples((currentSamples) => ({ ...currentSamples, ...revisedExamples })),
    loadVisitTemplate,
    getInstructionAssistantSectionMetadata,
    instructionAssistantUpdate,
  };
  // Which tools the chat gets follows what this clinician can already do here.
  // Writing into the EMR from chat is off for Confluent for now.
  const agenticChatOn = !!apiClient.features?.agentic_chat;
  const accountActive = apiClient.actionAllowed?.() !== false;
  const chatCanWriteEmr = accountActive && !apiClient.isConfluentUser;
  const chatAgentTools = useMemo(
    () =>
      agenticChatOn
        ? createVisitNoteTools(() => chatAgentHostRef.current, {
            canWrite: chatCanWriteEmr,
            canEditTemplate: showInstructionAssistant,
          })
        : null,
    [agenticChatOn, chatCanWriteEmr, showInstructionAssistant],
  );
  // Told to the model, so it knows up front what it can't do here and why
  const chatAgentContext = [
    (!configID || configID === -1) &&
      'At the start of this turn, no template was loaded for this visit. Call load_template once before get_template or any template-editing tool. It will discover and attach the matching template without running Comprehend. After it succeeds, retry the original tool; do not tell the clinician to run Comprehend just to attach a template.',
    !showInstructionAssistant &&
      `This visit's template is shared with the clinician as ${viewMode || 'read-only'}: you can read its instructions, examples and reply types, but neither you nor they can change them from here (the Instruction Assistant can't either), so don't offer to. To change it, the template's owner can, or the clinician can duplicate it in Note Settings to make their own copy.`,
    !accountActive && 'This account is not active, so you cannot change the note from chat.',
    accountActive &&
      apiClient.isConfluentUser &&
      "Writing into the EMR from chat is turned off for this clinician's organization: you can't write or re-run note sections. Say what to change and they can make the change in the EMR or with the panel's buttons.",
  ].filter(Boolean);

  useLayoutEffect(() => {
    // New Visit reuses this component, so discard visit-scoped undo data before paint.
    resetInstructionAssistantUpdate();
    setInstructionAssistantOpen(false);
    setInstructionAssistantFeedback('');
    setFeedbackRun(null);
  }, [location.hash, resetInstructionAssistantUpdate]);

  return (
    <>
      <style>{`
        @keyframes compliance-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        /* The note area loads slowly (the visit, then the EMR read): a shimmer
           while it does, and a soft fade-in when it arrives, never a jump. */
        @keyframes cpt-fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .cpt-fade-in { animation: cpt-fade-in .28s ease-out both; }
        @keyframes cpt-shimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }
        .cpt-skeleton { border-radius: 8px; background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%); background-size: 200% 100%; animation: cpt-shimmer 1.3s ease-in-out infinite; }
        /* Comprehend on its own row (Dictation present) vs all on one line. */
        .capture-actions.capture-stack { display: flex; flex-direction: column; align-items: stretch; gap: 6px; }
        .capture-actions.capture-inline { display: flex; flex-direction: row; align-items: stretch; gap: 6px; }
        .capture-actions.capture-inline > * { flex: 1 1 0; min-width: 0; }
        /* --- Capture block: Live Visit / Dictation split buttons + Comprehend --- */
        .capture-actions .capture-row { display: flex; align-items: stretch; gap: 6px; }
        .capture-actions .capture-row > * { flex: 1 1 0; min-width: 0; }
        /* Chevrons are a fixed sliver; the wide half of each split button takes the rest. */
        .capture-actions .cx-chevron-button,
        .capture-actions .mic-split-group > button + button:last-of-type {
          width: 1.5rem !important; min-width: 1.5rem !important; flex: 0 0 1.5rem !important;
          padding-left: 0 !important; padding-right: 0 !important;
          display: inline-flex; align-items: center; justify-content: center;
        }
        /* Scoped to .capture-cell on purpose: DictationButton's own .mic-split-group
           is a row child and must keep the flex: 1 1 0 above, or it takes the row. */
        .capture-actions .capture-cell .mic-split-group { flex: 1 1 auto; min-width: 0; }
        .capture-actions .mic-split-group > button:first-of-type { flex: 1 1 auto; min-width: 0; padding-right: 10px; }
        .capture-actions .mic-label-stack { width: auto !important; flex: 1 1 auto; }
        /* Comprehend full width; stays a positioned ancestor for its menu. */
        .capture-actions .capture-comprehend { position: relative; display: block; width: 100%; }
        .capture-actions .capture-comprehend > .dropdown-container,
        .capture-actions .capture-comprehend > button { width: 100%; }
        /* Narrow surfaces: reclaim padding + icon + label so two-word labels fit
           instead of being sliced at the chevron. !important beats inline styles. */
        .capture-compact .mic-split-group > button:first-of-type { padding-left: 6px; padding-right: 6px; gap: 4px; }
        .capture-compact .mic-split-group > button:first-of-type > .fa { font-size: 1.4em !important; margin-right: 3px !important; }
        .capture-compact .mic-label-stack > span:first-child {
          font-size: 13px !important; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
        }
      `}</style>
      <LoadingBanner showBanner={aiThinking} loadingText={bannerText} />
      {showInstructionAssistant && (
        <InstructionAssistantModal
          isOpen={instructionAssistantOpen}
          onClose={() => {
            setInstructionAssistantOpen(false);
            setInstructionAssistantFeedback('');
          }}
          apiClient={apiClient}
          configId={configID}
          getSectionMetadata={getInstructionAssistantSectionMetadata}
          onSaveToTemplate={instructionAssistantUpdate.applyTemplate}
          onSaveToTemplateAndNote={instructionAssistantUpdate.applyTemplateAndNote}
          visitTranscript={transcript}
          compact
          initialFeedback={instructionAssistantFeedback}
        />
      )}
      <InstructionUpdateUndoModal
        isOpen={instructionAssistantUpdate.state.showInstructionModal}
        kind={instructionAssistantUpdate.state.kind}
        oldInstructions={instructionAssistantUpdate.state.oldInstructions}
        newInstructions={instructionAssistantUpdate.state.newInstructions}
        oldExamples={instructionAssistantUpdate.state.oldExamples}
        newExamples={instructionAssistantUpdate.state.newExamples}
        onResolveInstructions={instructionAssistantUpdate.resolveUndo}
      />
      <Header isLite={true} flush />
      {/* New Patient Modal */}
      <PatientModal
        isOpen={isPatientModalOpen}
        onClose={() => setPatientModelOpen(false)}
        onSave={newPatient}
      ></PatientModal>
      {patientMismatch && (
        <Modal
          isOpen={!!patientMismatch}
          onClose={() => setPatientMismatch(null)}
          buttons={[
            {
              label: 'Comprehend Anyway',
              color: 'secondary',
              onClick: async () => {
                const { key, updateSectionStatusIndicator, source } =
                  patientMismatch;
                setPatientMismatch(null);
                await comprehendTemplate(key, updateSectionStatusIndicator, {
                  skipPatientMatchCheck: true,
                  source,
                });
              },
            },
            {
              label: 'Go Back',
              color: 'primary',
              onClick: () => {
                setPatientMismatch(null);
              },
            },
          ]}
        >
          <h2>Patient Mismatch ⚠️</h2>
          <p>
            This visit is for{' '}
            <strong>{patientMismatch.visitPatientName}</strong>, but the chart
            open in your EMR
            {patientMismatch.emrPatientName
              ? ` is for ${patientMismatch.emrPatientName}`
              : ' is for a different patient'}
            .
          </p>
          <p>
            Open the correct patient in your EMR, or comprehend anyway if you
            know this is right.
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="dontShowPatientMismatch"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#222',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                id="dontShowPatientMismatch"
                style={{
                  margin: 0,
                  width: '16px',
                  height: '16px',
                  verticalAlign: 'middle',
                  cursor: 'pointer',
                }}
                defaultChecked={
                  localStorage.getItem(PATIENT_MATCH_DISMISSED_KEY) === 'true'
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    localStorage.setItem(PATIENT_MATCH_DISMISSED_KEY, 'true');
                  } else {
                    localStorage.removeItem(PATIENT_MATCH_DISMISSED_KEY);
                  }
                }}
              />
              Don't show this again
            </label>
          </div>
        </Modal>
      )}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        buttons={modalButtons}
        isCloseButton={false}
      >
        <h2>{modalHeader}</h2>
        <p>{modalText}</p>
      </Modal>
      <Modal
        isOpen={showTemplateHelpModal}
        onClose={() => setShowTemplateHelpModal(false)}
      >
        <p>
          We match the template we scanned from your EMR 1-to-1. Example, if you
          are looking at an "Initial Eval" in your EMR, we select your "Initial
          Eval" Comprehend Template.
        </p>
      </Modal>
      {showHighlightModal && (
        <Modal
          isOpen={showHighlightModal}
          onClose={() => setShowHighlightModal(false)}
          buttons={[
            {
              label: 'Comprehend',
              color: 'secondary',
              onClick: () => {
                handleComprehendClick(true);
                setShowHighlightModal(false);
              },
            },
            {
              label: 'Go Back',
              color: 'primary',
              onClick: () => {
                setShowHighlightModal(false);
              },
            },
          ]}
        >
          <h2>Review Highlighted Fields</h2>
          <p style={{ marginBottom: '16px' }}>
            For Yes/No questions, choose your answer before clicking Comprehend.
            Only visible sections on your note will be filled in.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="dontShowAgain"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#222',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                id="dontShowAgain"
                style={{
                  margin: 0,
                  width: '16px',
                  height: '16px',
                  verticalAlign: 'middle',
                  cursor: 'pointer',
                }}
                defaultChecked={
                  localStorage.getItem('comprehend-highlight-modal') === 'true'
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    localStorage.setItem('comprehend-highlight-modal', 'true');
                  } else {
                    localStorage.removeItem('comprehend-highlight-modal');
                  }
                }}
              />
              Don't show this again
            </label>
          </div>
        </Modal>
      )}
      {/* Page content: edge to edge in the side panel (below 768px: no margin,
          no card frame, cpt-visit-flush); wide screens keep the usual layout. */}
      <Container className="mt--7 cpt-visit-flush" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <NavigationPath paths={breadcrumbItems} />
              <CardHeader className="border-0" style={{ padding: '0.4rem' }}>
                <div style={{ flex: '1' }}>
                  {' '}
                  <div>
                    {isOnline ? (
                      ''
                    ) : (
                      <span style={{ color: 'blue', fontSize: '0.95rem' }}>
                        Unstable Internet connection was detected, might affect
                        your transcript!
                      </span>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: '100%',
                  }}
                >
                  {apiClient.features?.[COMPREHEND_FEEDBACK_FLAG] &&
                  feedbackRun &&
                  feedbackRun.conversationId === conversationID && (
                    <div style={{ margin: '0 -0.4rem' }}>
                      <ComprehendFeedback
                        key={feedbackRun.id}
                        sectionChoices={feedbackRun.generatedSections}
                        onSubmit={handleComprehendFeedback}
                        onDone={() => setFeedbackRun(null)}
                      />
                    </div>
                  )}
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <Label id="status" style={{ fontSize: '0.95rem' }}>
                      {status}
                    </Label>
                  </div>

                  {/* With Dictation on screen: capture buttons on top,
                      Comprehend full width underneath. Without it: all on one
                      line. Same markup either way - only the class changes. */}
                  <div
                    className={`cx-record-comprehend-actions capture-actions capture-compact ${
                      showDictationButton ? 'capture-stack' : 'capture-inline'
                    }`}
                  >
                    <div className="capture-row">
                      <div className="cx-record-control capture-cell">
                        <AudioRecorder
                          recordButtonId="audio-recorder-button"
                          conversationID={conversationID}
                          refresh={updateConversationDetails}
                          setStatus={_setStatus}
                          seconds_transcribed={secondsTranscribed} // Pass the value of seconds_transcribed
                          // Visit wording only once dictation is enabled; without it
                          // the button keeps the Record/Resume defaults.
                          startLabel={showDictation ? 'Live Visit' : undefined}
                          resumeLabel={showDictation ? 'Resume Visit' : undefined}
                        />
                      </div>

                      {/* Dictation only makes sense once Clinical Facts is on
                          screen — that's where the transcribed text lands. */}
                      {showDictationButton && (
                        <DictationButton
                          apiClient={apiClient}
                          onText={appendVisitNotes}
                        />
                      )}
                    </div>

                    {/* Comprehend Button */}

                    <div className="capture-comprehend">
                      <div
                        style={{
                          display: 'flex',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                        className="dropdown-container"
                      >
                        <Button
                          color="primary"
                          size="med"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0',
                            border: 'none',
                            marginRight: '0',
                            // padding: '8px',
                            padding: '8px',
                            flex: '1',
                            whiteSpace: 'nowrap',
                            opacity: disableComprehend ? 0.6 : 1,
                            cursor: disableComprehend
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                          onClick={() => handleComprehendClick(false)}
                          id="comprehend-button"
                          disabled={disableComprehend}
                        >
                          <img
                            alt="Comprehend"
                            src={require('../assets/img/brand/mascot_light.png')}
                            style={{
                              height: '2rem',
                              marginRight: '4px',
                              flexShrink: 0,
                            }}
                          />
                          Comprehend
                        </Button>
                        {!disableComprehend && emrUndoStack.length > 0 && (
                          <Button
                            className="cx-chevron-button"
                            color="primary"
                            size="med"
                            style={{
                              borderRadius: '0',
                              padding: '0',
                              minWidth: '1.5rem',
                              width: '1.5rem',
                              flex: '0 0 1.5rem',
                              marginLeft: '0',
                              border: 'none',
                              borderLeft: '1px solid rgba(255,255,255,0.2)',
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowDropdown((open) => !open);
                            }}
                            title="More Comprehend actions"
                            aria-label="More Comprehend actions"
                            aria-expanded={showDropdown}
                          >
                            <span className="fa fa-chevron-down" />
                          </Button>
                        )}
                      </div>
                      {/* Dropdown Menu */}
                      {showDropdown && !disableComprehend && emrUndoStack.length > 0 && (
                        <div
                          className="dropdown-container"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            right: '0',
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            marginTop: '2px',
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              border: 'none',
                              borderBottom: '1px solid #eee',
                              backgroundColor: 'white',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRevertChanges && handleRevertChanges();
                              setShowDropdown(false);
                            }}
                          >
                            <span>🔄</span>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>
                                Revert Changes
                              </div>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                Revert all the changes made in this visit
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              {/* Tabs header uses reusable utility: .tabs-scroll-container (styles in argon-dashboard-react.scss) */}
              <div className="tabs-scroll-container" ref={tabsScrollRef}>
                {showTabLeft && (
                  <>
                    <div
                      className="tabs-scroll-fade left"
                      style={{ height: tabsBarH || undefined }}
                    ></div>
                    <div
                      className="tabs-scroll-btn left"
                      style={{
                        top: tabsBarH
                          ? `${Math.max(0, Math.round(tabsBarH / 2 - 11))}px`
                          : undefined,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const nav = tabsNavRef.current;
                        if (!nav) return;
                        nav.scrollBy({ left: -120, behavior: 'smooth' });
                      }}
                    >
                      <i
                        className="fa fa-chevron-left"
                        style={{ fontSize: '0.8rem', color: '#666' }}
                      ></i>
                    </div>
                  </>
                )}
                {showTabRight && (
                  <>
                    <div
                      className="tabs-scroll-fade right"
                      style={{ height: tabsBarH || undefined }}
                    ></div>
                    <div
                      className="tabs-scroll-btn right"
                      style={{
                        top: tabsBarH
                          ? `${Math.max(0, Math.round(tabsBarH / 2 - 11))}px`
                          : undefined,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const nav = tabsNavRef.current;
                        if (!nav) return;
                        nav.scrollBy({ left: 120, behavior: 'smooth' });
                      }}
                    >
                      <i
                        className="fa fa-chevron-right"
                        style={{ fontSize: '0.8rem', color: '#666' }}
                      ></i>
                    </div>
                  </>
                )}
                <Tabs
                  id="conversation-tabs"
                  activeKey={activeTab}
                  onSelect={(key) => setActiveTab(key)}
                  className="mb-3 custom-tabs"
                >
                  {renderWorkspaceTabs(workspace, [
                  /* Response Tab */
                  <Tab
                    eventKey="response"
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <WorkspaceIcon id="note" size={17} style={{ marginRight: '6px' }} />
                        <h4 style={{ margin: 0 }}>Note</h4>
                      </div>
                    }
                  >
                    <div className="list-container">
                      {/* <ul> */}
                      <ul
                        style={{
                          paddingInlineStart: '8px',
                          paddingInlineEnd: '8px',
                        }}
                      >
                        <Collapsible
                          id="transcript-section-collapsible"
                          label="Transcript"
                          content={transcript}
                          setContent={setTranscript}
                          setStatus={_setStatus}
                          conversationID={conversationID}
                          setRefresh={setRefresh}
                          onWriteNotesPencilClick={toggleWriteNotes}
                        />
                        {showWriteNotes && (
                          <WriteNotesCollapsible
                            content={visitNotes}
                            setContent={setVisitNotes}
                          />
                        )}
                        {/* Vendor Apps: what connected apps told us about this patient — its own field,
                            shown whenever there is any, independent of the Clinical Facts toggle. Read-only: the app's latest data wins. */}
                        {isVendorAppsEnabled(apiClient) && <AppData appContext={appContext} />}
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap', // Allow wrapping when necessary
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px', // Space between elements
                            marginTop: '10px',
                          }}
                        >
                          <Modal
                            isOpen={!!showEditorFields}
                            onClose={() => setShowEditorFields(false)}
                            isCloseButton={false}
                            onEnterClose={true}
                            hideCross={true}
                          >
                            <h4>
                              {showEditorFields === 'visit'
                                ? 'Edit Visit Name'
                                : 'Edit Patient Name'}
                            </h4>
                            <div
                              id="outline-selector-patient"
                              style={{
                                marginTop: '20px',
                                flexGrow: 1,
                                flexShrink: 1,
                                flexBasis: '45%',
                                minWidth: '100px',
                              }}
                            >
                              {showEditorFields === 'visit' && (
                                <EditableLabel
                                  labelText="Visit Name"
                                  text={name}
                                  onChange={setName}
                                  className="h4"
                                  inputRef={inputRef}
                                />
                              )}
                              {showEditorFields === 'patient' && (
                                <OutlinedSelector
                                  label="Patient"
                                  options={uniqueGroups}
                                  value={group}
                                  onChange={(val) => {
                                    setGroup(val);
                                    setShowEditorFields(false);
                                  }}
                                />
                              )}
                            </div>
                          </Modal>
                        </div>
                        {status === defaultStatus ? (
                          // The visit is still loading: hold its place with a shimmer.
                          <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
                            <div className="cpt-skeleton" style={{ height: 42 }} />
                            <div className="cpt-skeleton" style={{ height: 30, width: '86%' }} />
                            <div className="cpt-skeleton" style={{ height: 30, width: '72%' }} />
                            <div className="cpt-skeleton" style={{ height: 30, width: '80%' }} />
                          </div>
                        ) : (
                          // The note (template, Scan, Learn, sections) shows on every
                          // visit, recorded or not. While the note type is still being
                          // read from the EMR, the Comprehend ⇄ EMR card holds its space
                          // and the note waits, hidden, then simply appears.
                          <div className="cpt-fade-in">
                            {emrConnecting && <EmrConnecting emr={apiClient.EMR} />}
                            <div style={{ display: emrConnecting ? 'none' : undefined }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'stretch',
                                gap: '10px',
                              }}
                            >
                              {/* Group EMR Template and OptionsDropdown */}
                              <div
                                style={{
                                  display: 'flex',
                                  flexGrow: 1,
                                  flexShrink: 1,
                                  width: '100%',
                                  maxWidth: '100%', // Prevent overflow from container
                                  alignItems: 'center', // Ensure both elements align vertically
                                  gap: '10px', // Add spacing between the template and dropdown
                                }}
                              >
                                <div
                                  id="outline-selector-template"
                                  style={{
                                    flexGrow: 1,
                                    minWidth: '160px',
                                    marginLeft: '0px',
                                  }} // Ensure it grows and doesn't overflow
                                  onClick={(e) => {
                                    if (!apiClient.showAllEMRTemplates) {
                                      e.stopPropagation();
                                      setShowTemplateHelpModal(true);
                                    }
                                  }}
                                >
                                  {Array.isArray(uniqueConfigs) &&
                                  uniqueConfigs.length > 0 ? (
                                    <OutlinedSelector
                                      label="EMR Note Type"
                                      options={uniqueConfigs}
                                      value={config}
                                      onChange={(selectedValue) => {
                                        if (
                                          !selectedValue.startsWith('(') &&
                                          selectedValue !== '---------------'
                                        ) {
                                          userChangedConfig(selectedValue); // Only allow valid selections
                                        }
                                      }}
                                      disabled={!apiClient.showAllEMRTemplates}
                                    />
                                  ) : (
                                    <p>Load visit note to get templates</p>
                                  )}
                                </div>
                                <div
                                  style={{
                                    flexGrow: 0, // Do not allow it to grow
                                    flexShrink: 1, // Allow shrinking to prevent overflow
                                    minWidth: '50px', // Minimum width to prevent collapsing
                                    maxWidth: '100px', // Prevent it from overflowing
                                  }}
                                >
                                  <OptionsDropdown
                                    disableEditing={disableEditing}
                                    apiClient={apiClient}
                                    configId={configID}
                                    userEmail={userEmail}
                                    conversationID={conversationID}
                                  />
                                </div>
                              </div>
                              {/* Left column: Sync + Learn stacked */}
                              <div
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  flexWrap: 'wrap', // optional, handles responsiveness
                                  gap: '10px',
                                }}
                              >
                                <Button
                                  color="primary"
                                  size="med"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: 0,
                                    marginRight: 0,
                                    flex: showLearnButton
                                      ? '0 0 56px'
                                      : '1 1 auto',
                                    width: showLearnButton ? '56px' : '100%',
                                    minWidth: '56px',
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                  }}
                                  onClick={loadTemplate}
                                  id="scrape-button"
                                >
                                  <i
                                    className="fa fa-magnifying-glass-chart"
                                    style={{
                                      marginRight: '4px',
                                      fontSize: '1rem',
                                    }}
                                  ></i>
                                </Button>

                                {showLearnButton && (
                                  <Button
                                    color="primary"
                                    size="med"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginLeft: 0,
                                      marginRight: 0,
                                      flex: '1 1 auto',
                                    }}
                                    onClick={handleLearnTemplate}
                                    id="learn-button"
                                  >
                                    <i
                                      className="fa fa-brain"
                                      style={{
                                        marginRight: '4px',
                                        fontSize: '1rem',
                                      }}
                                    ></i>
                                    Learn
                                  </Button>
                                )}

                                {showInstructionAssistant && (
                                  <Button
                                    color="primary"
                                    size="med"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginLeft: 0,
                                      marginRight: 0,
                                      flex: '0 0 56px',
                                      width: '56px',
                                      minWidth: '56px',
                                      paddingLeft: 0,
                                      paddingRight: 0,
                                    }}
                                    onClick={() => {
                                      setInstructionAssistantFeedback('');
                                      setInstructionAssistantOpen(true);
                                    }}
                                    id="instruction-assistant-button"
                                    title="Instruction Assistant"
                                    aria-label="Instruction Assistant"
                                  >
                                    <i
                                      className="fa fa-wand-magic-sparkles"
                                      style={{ fontSize: '1rem' }}
                                    ></i>
                                  </Button>
                                )}
                              </div>
                              {instructionAssistantUpdate.state.status !==
                                'idle' && (
                                <div
                                  style={{
                                    marginTop: '10px',
                                    maxWidth: '420px',
                                    width: '100%',
                                  }}
                                >
                                  <InstructionUpdateStatusStrip
                                    status={
                                      instructionAssistantUpdate.state.status
                                    }
                                    kind={instructionAssistantUpdate.state.kind}
                                    onUndo={instructionAssistantUpdate.openUndo}
                                    compact
                                  />
                                </div>
                              )}
                            </div>
                            <div id="all-sections">
                              {Object.keys(templateFiltered).map((key, index) => {
                                return (
                                  <div key={key}>
                                  <Collapsible
                                    id={`section-collapsible-${index}`}
                                    label={key}
                                    content={templateFiltered[key]}
                                    setContent={(newAnswer) =>
                                      updateTemplate(key, newAnswer)
                                    }
                                    onAddSample={() =>
                                      onAddSample(
                                        key,
                                        configID,
                                        _setSamples,
                                        apiClient,
                                      )
                                    }
                                    onEditSample={(index, newText) =>
                                      onEditSample(
                                        key,
                                        index,
                                        newText,
                                        configID,
                                        _setSamples,
                                        apiClient,
                                      )
                                    }
                                    samples={samples}
                                    openConfirmationModal={
                                      openConfirmationModal
                                    }
                                    comprehend={(sectionKey, updateStatus) =>
                                      comprehendTemplate(sectionKey, updateStatus, {
                                        source: FEEDBACK_SOURCES.sectionRefresh,
                                      })
                                    }
                                    questionKey={key}
                                    configID={configID}
                                    sectionText={key}
                                    disabled={disableEditing}
                                    isNoReply={
                                      templateReply?.[key] === 'no_reply'
                                    }
                                    templateReply={templateReply}
                                    setTemplateReply={setTemplateReply}
                                    showSelector={true}
                                    subsections={
                                      subsections && subsections[key]
                                        ? subsections[key]
                                        : null
                                    }
                                  ></Collapsible>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Vendor Apps: what each connected app is asking of this visit, as a subsection under the template's own sections. */}
                            {isVendorAppsEnabled(apiClient) && <VendorSubscriptionsSection appContext={appContext} />}
                            </div>
                          </div>
                        )}
                      </ul>
                    </div>
                  </Tab>,
                  /* Chat Tab */
                  <Tab
                    eventKey="chat"
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <WorkspaceIcon id="chat" size={17} style={{ marginRight: '6px' }} />
                        <span style={{ fontSize: '1rem', fontWeight: 500 }}>
                          Ask
                        </span>
                      </div>
                    }
                  >
                    {conversationID ? (
                      <Chat
                        conversationId={conversationID}
                        apiClient={apiClient}
                        groupId={groupId}
                        isActive={workspace.isShown('chat')}
                        agentTools={chatAgentTools}
                        agentContext={chatAgentContext}
                        // agentic_chat: input pinned to the bottom of the panel; Help and checklist buttons hidden while chatting
                        inVisitPanel={agenticChatOn}
                      />
                    ) : (
                      <p>Loading chat...</p>
                    )}
                  </Tab>,
                  /* Compliance Tab */
                  canShowCompliance && (
                    <Tab
                      eventKey="compliance"
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {complianceScoreLoading ? (
                            <i
                              className="fa fa-spinner fa-spin"
                              style={{
                                height: '1.2rem',
                                width: '1.2rem',
                                fontSize: '1.2rem',
                              }}
                            />
                          ) : complianceScore ? (
                            <>
                              {/* The shared scale: components/Misc/complianceGrades.js */}
                              <span
                                style={{
                                  color: GRADE_TEXT_COLORS[gradeForScore(complianceScore)],
                                  marginRight: '5px',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                }}
                              >
                                {gradeForScore(complianceScore)}
                              </span>
                            </>
                          ) : (
                            <WorkspaceIcon id="compliance" size={17} style={{ marginRight: '6px' }} />
                          )}
                          <span style={{ fontSize: '1rem', fontWeight: 500 }}>
                            {scoreSurfaceLabel}
                          </span>
                        </div>
                      }
                    >
                      {canShowCompliance && (
                        <div
                          id="compliance-score"
                          style={{
                            flex: 'none',
                            alignSelf: 'center', // Center alignment on smaller screens
                            width: '100%',
                          }}
                        >
                          {/* Get Compliance Score Button */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '10px',
                              padding: '20px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                            >
                              <Button
                                color="primary"
                                onClick={handleGetComplianceScore}
                                disabled={complianceScoreLoading}
                                style={{
                                  borderRadius: '20px',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '10px 16px',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                {complianceScoreLoading ? (
                                  <>
                                    <i
                                      className="fa fa-spinner fa-spin"
                                      style={{ fontSize: '1.1rem' }}
                                    ></i>
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fa fa-refresh"
                                      style={{
                                        fontSize: '1.1rem',
                                        marginRight: '6px',
                                      }}
                                    ></i>
                                    {scoreRefreshLabel}
                                  </>
                                )}
                              </Button>
                              <i
                                id="compliance-score-info-icon"
                                className="fa fa-info-circle"
                                style={{
                                  fontSize: '0.9rem',
                                  cursor: 'help',
                                  color: '#5e72e4',
                                }}
                              ></i>
                            </div>
                            <Tooltip
                              placement="top"
                              target="compliance-score-info-icon"
                              id="compliance-score-info-tooltip"
                            >
                              Pulls your updated note from the EMR re-calculates
                              your {scoreSurfaceLabel.toLowerCase()} score.
                            </Tooltip>
                          </div>
                          <ComplianceScore
                            score={complianceScore}
                            height="120px"
                            feedback={complianceFeedback}
                          />
                          {isConfluentUser && (
                            <p
                              style={{
                                fontSize: '11px',
                                color: '#9ca3af',
                                textAlign: 'center',
                                marginTop: '8px',
                                marginBottom: '4px',
                              }}
                            >
                              Powered by Comprehend PT
                            </p>
                          )}
                        </div>
                      )}
                    </Tab>
                  ),
                  /* Coding Tab */
                  apiClient.features?.show_coding_tab && (
                    <Tab
                      eventKey="coding"
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <WorkspaceIcon id="coding" size={17} style={{ marginRight: '6px' }} />
                          <span style={{ fontSize: '1rem', fontWeight: 500 }}>
                            Coding
                          </span>
                        </div>
                      }
                    >
                      {conversationID ? (
                        <Coding
                          key={conversationID}
                          conversationID={conversationID}
                          apiClient={apiClient}
                          initialResults={codingRecommendations}
                          initialPayerName={codingPayerName}
                          noteType={config}
                          autoRun={codingAutoRun}
                          getCurrentNote={getExistingValuesFromEMR}
                        />
                      ) : (
                        <p>Loading coding...</p>
                      )}
                    </Tab>
                  ),
                  ], activeTab, { compliance: complianceCompactTitle(complianceScore, complianceScoreLoading) })}
                </Tabs>
              </div>
              {/* Initialize tab scroll affordances on mount and when size changes */}
              {(() => {
                // Inline effect block kept minimal in-file
                if (typeof window !== 'undefined') {
                  setTimeout(() => {
                    const container = tabsScrollRef.current;
                    if (!container) return;
                    const nav = container.querySelector('.nav-tabs');
                    tabsNavRef.current = nav;
                    if (!nav) return;
                    const sync = () => {
                      setShowTabLeft(nav.scrollLeft > 0);
                      setShowTabRight(
                        nav.scrollLeft + nav.clientWidth < nav.scrollWidth,
                      );
                      setTabsBarH(nav.offsetHeight || 0);
                    };
                    nav.addEventListener('scroll', sync, { passive: true });
                    sync();
                    window.addEventListener('resize', sync);
                  }, 0);
                }
              })()}
            </Card>
          </div>
        </Row>
      </Container>
      <ToastContainer />
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction}
      >
        {confirmationDetails.type === 'sample'
          ? 'Are you sure you want to delete this sample?'
          : 'Are you sure you want to delete this template?'}
      </ConfirmationModal>
      <ShareTemplateModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        configID={configID}
        sharedAccessData={sharedAccessData}
      />
      <ConfirmationModal
        isOpen={popupVisible}
        onClose={() => setPopupVisible(false)}
        onConfirm={handleRateLimitConfirmation}
        confirmButtonText="Proceed with Downgraded Service"
      >
        Rate limit exceeded! Please wait {rateLimit} minutes before trying to
        generate a response or use a reduced model. The reduced model may impact
        the quality and accuracy of the generated notes.
      </ConfirmationModal>
    </>
  );
};


export default ConversationDetails;
